/**
 * ArchLang Parser (v0.2)
 *
 * Recursive descent parser that builds a ParsedFile of TopLevelDecls.
 *
 * Top-level grammar:
 *   program        = declaration*
 *   declaration    = type_decl | module_decl | process_decl | view_decl
 *
 * Module / surface / type bodies share a common shape parsed by `parseBody()`.
 * Interface bodies are parsed by `parseInterfaceBody()`.
 *
 * Order of body members is not significant — the parser collects each
 * member type into its own array on the resulting `Body`.
 */

import {
  EMPTY_BODY,
  EMPTY_INTERFACE_BODY,
  generateId,
  stableId,
} from "./types.js";
import type {
  DecorationArg,
  DecorationDecl,
  DecorationField,
  DecorationFieldType,
  DecorationInstance,
} from "./decorations.js";

/** Narrow a written type name to the decoration-local type set. */
function asDecorationFieldType(word: string): DecorationFieldType | undefined {
  return word === "marker" ||
    word === "text" ||
    word === "number" ||
    word === "bool" ||
    word === "ref" ||
    word === "list"
    ? word
    : undefined;
}
import type {
  AspectRoster,
  AwaitStep,
  BlockStep,
  Body,
  OnInclusion,
  PartialPresence,
  PresenceRef,
  CatchClause,
  DependencyEntry,
  DescriptionEntry,
  DoStep,
  DropStmt,
  EachStep,
  SurfaceDecl,
  FailStep,
  FieldDecl,
  FinishStep,
  GoStep,
  IfBranch,
  IfStep,
  InterfaceBody,
  InterfaceDecl,
  AspectEntry,
  MergeBool,
  MergeClause,
  ModuleDecl,
  Name,
  NoteStep,
  OwnerBlockStep,
  PackageManifest,
  ParallelBranch,
  ParallelFanOut,
  ParallelStep,
  ParsedFile,
  ProcessDecl,
  ProcessStep,
  QualifiedName,
  ReversibleItem,
  ReversibleStep,
  RetryTail,
  SelectCase,
  SelectCount,
  SelectStep,
  SimpleStep,
  SourceSpan,
  StableId,
  StepOwner,
  SubprocessDecl,
  TopLevelDecl,
  TryStep,
  TypeDecl,
  UnwindClause,
  UseDecl,
  UseImport,
  Value,
} from "./types.js";
import type { Token, TokenType } from "./lexer.js";
import { RESERVED_KEYWORDS, tokenize } from "./lexer.js";
import { STDLIB_INTERFACE_KINDS, STDLIB_SURFACE_KINDS } from "./generated/stdlib-kinds.js";
import type { LensDecl, PolicyDecl, QueryDecl, StyleBundleDecl, TestDecl, UsePolicyStmt, ViewDeclV2 } from "./v010-ast.js";
import { V010DeclParser } from "./v010-decl-parser.js";
import { SelectorExprParser } from "./selector-parser.js";
import { EXPRESSION_FUNCTIONS } from "./selector-ast.js";
import { renderDiagnostic } from "./diagnostics/catalog.js";

/** Literal brace fragments passed as ICU params — a bare `{`/`}` in a template
 *  is an ICU argument delimiter (throws MALFORMED_ARGUMENT), so every parse
 *  message that quotes a brace supplies it through these. */
const OPEN_BRACE = "'{'";
const CLOSE_BRACE = "'}'";

// =============================================================================
// Public API
// =============================================================================

export interface ParseError {
  /** English rendering — always present, the fallback when `msgKey` is unset. */
  readonly message: string;
  readonly span?: SourceSpan;
  /** Catalog key + params so parse errors render per-locale like semantic
   *  diagnostics (see diagnostics/catalog.ts). `code` is always "PARSE_ERROR". */
  readonly code?: string;
  readonly msgKey?: string;
  readonly params?: Readonly<Record<string, unknown>>;
}

export interface ParseResult {
  readonly file: ParsedFile;
  readonly tokens: readonly Token[];
  readonly errors: readonly ParseError[];
}

export function parse(source: string, file?: string): ParseResult {
  const lex = tokenize(source, file);
  const parser = new Parser(lex.tokens, source);
  const result = parser.parseFile();
  const errors: ParseError[] = [
    ...lex.errors.map((e) => ({ ...e })),
    ...result.errors,
  ];
  return { file: result.file, tokens: lex.tokens, errors };
}

/** Subset of `ParseResult` worth caching — the AST + errors. Tokens are
 *  discarded (only the lexer/formatter need them, and they'd bloat the cache). */
export interface CachedParse {
  readonly file: ParsedFile;
  readonly errors: readonly ParseError[];
}

const PARSE_CACHE_MAX = 4096;
interface ParseCacheEntry {
  readonly source: string;
  readonly parse: CachedParse;
}
const parseCache = new Map<string, ParseCacheEntry>();

/**
 * Per-path parse cache (B2 incremental parse). `parse` is a pure,
 * deterministic function of (source, file) — every id derives from the source
 * via `stableId`, and the parser never calls the non-deterministic
 * `generateId` — so a cached AST is byte-identical to a fresh one and safe to
 * reuse across calls. Loaders (viewer-core, lsp) call this per `.arch` file, so
 * re-running the pipeline after an edit only re-parses the file that changed;
 * unchanged files hit the cache.
 *
 * Keyed by path, holding only the LATEST content per path: editor keystrokes
 * flush a new content version per debounce, and retaining every version's AST
 * (the old content-addressed keying) leaked hundreds of KB per edit in the
 * browser. Live consumers of an older version hold their ASTs directly — the
 * cache only needs to serve the pipeline's repeated parses of the CURRENT
 * content — so a source mismatch re-parses and replaces. (Two loaders
 * alternating different content at the same path — e.g. a two-sided diff —
 * thrash the entry and re-parse; bounded, one-shot cost.) The path embeds
 * into spans, so identical content at different paths gets distinct entries
 * by construction. Callers must treat the returned `file` as READ-ONLY — it
 * is shared. LRU-bounded on path count as a backstop.
 */
export function parseCached(source: string, file?: string): CachedParse {
  const key = file ?? "";
  const hit = parseCache.get(key);
  if (hit && hit.source === source) {
    parseCache.delete(key); // LRU bump
    parseCache.set(key, hit);
    return hit.parse;
  }
  const r = parse(source, file);
  const entry: ParseCacheEntry = { source, parse: { file: r.file, errors: r.errors } };
  parseCache.delete(key);
  parseCache.set(key, entry);
  if (parseCache.size > PARSE_CACHE_MAX) {
    const oldest = parseCache.keys().next().value;
    if (oldest !== undefined) parseCache.delete(oldest);
  }
  return entry.parse;
}

/** Clear the parse cache (tests; the cache otherwise self-bounds). */
export function _resetParseCache(): void {
  parseCache.clear();
}

export interface ManifestParseResult {
  readonly manifest: PackageManifest;
  readonly tokens: readonly Token[];
  readonly errors: readonly ParseError[];
}

/**
 * Parse a `package.archspace` source. Different top-level grammar than
 * regular `.arch` files: only `name`, `dependencies`, and `use` declarations
 * are allowed at root.
 */
export function parsePackageManifest(
  source: string,
  file?: string
): ManifestParseResult {
  const lex = tokenize(source, file);
  const parser = new Parser(lex.tokens);
  const result = parser.parseManifest();
  const errors: ParseError[] = [
    ...lex.errors.map((e) => ({ ...e })),
    ...result.errors,
  ];
  return { manifest: result.manifest, tokens: lex.tokens, errors };
}

// =============================================================================
// Parser implementation
// =============================================================================

class Parser {
  private readonly tokens: readonly Token[];
  private pos = 0;
  private readonly errors: ParseError[] = [];
  /** v0.9.0 §1.2: inclusion refs collected from top-level `on` blocks. */
  private onInclusions: OnInclusion[] = [];
  /** Original source — threaded so v0.10 capability getters can raw-scan args. */
  private readonly source?: string;

  constructor(tokens: readonly Token[], source?: string) {
    this.tokens = tokens;
    this.source = source;
  }

  // -------------------------------------------------------------------------
  // Top-level
  // -------------------------------------------------------------------------

  parseFile(): { file: ParsedFile; errors: readonly ParseError[] } {
    const declarations: TopLevelDecl[] = [];
    const uses: UseDecl[] = [];
    this.onInclusions = [];
    const startTok = this.peek();
    const startSpan = startTok.span;

    while (!this.isAtEnd()) {
      try {
        // Tolerate a stray top-level `;` (inline step separator left between
        // bare steps or declarations) instead of erroring on it.
        if (this.matchToken("SEMICOLON")) continue;
        // v0.10: `use policy <Name> from <pkg>` — governance instantiation, NOT
        // an import (`use policy from X` still imports a thing named `policy`).
        if (this.isUsePolicyAhead()) {
          declarations.push({ kind: "usePolicy", decl: this.parseV010UsePolicy() });
          continue;
        }
        if (this.check("USE") || (this.check("EXPORT") && this.peekAhead(1)?.type === "USE")) {
          uses.push(this.parseUseDecl());
          continue;
        }
        // `@name(args)` above a `use` import. Parsed and REFUSED: an import
        // resolves to no model node, so there is nothing for a decoration to
        // annotate — and the thing an author means to mark (the imported
        // element) is declared in the package it comes from, which is where
        // the annotation belongs and where every reader of that element will
        // see it. Consumed rather than left to the next branch so the
        // decoration cannot silently bind to whatever declaration follows.
        if (this.check("AT") && this.useAheadOfDecorations()) {
          const start = this.peek().span;
          this.parseDecorationPrefix();
          this.errorAt(start, "parse.decorationOnUse");
          uses.push(this.parseUseDecl());
          continue;
        }
        // `@deco* on <planes> …` — decorations are the OUTERMOST prefix, so
        // they wrap the plane prefix. Handled HERE rather than in the `AT`
        // branch of `parseDeclaration`, because plane presence can yield many
        // declarations (the block form) while `parseDeclaration` returns one;
        // every member gets the decorations stamped on it. Without this the
        // documented spelling failed with "Unexpected token in top level: on".
        if (this.check("AT") && this.onAheadOfDecorations()) {
          const at = this.peek();
          const decorations = this.parseDecorationPrefix();
          const exported = this.matchToken("EXPORT") !== undefined;
          const scoped: TopLevelDecl[] = [];
          this.parseOnPresence(scoped, exported);
          for (const d of scoped) {
            const stamped = this.stampDecorations(d, decorations, at.span);
            if (stamped) declarations.push(stamped);
          }
          continue;
        }
        // v0.9.0 §1.2: `on <planes> <decl>` / `on <planes> { … }` — born-on
        // presence. A block yields many declarations; pushed directly.
        if (this.check("ON")) {
          this.parseOnPresence(declarations);
          continue;
        }
        // `export on <planes> <decl>` (GRAMMAR: `["export"] [on_prefix] decl`).
        // A place on a plane is the likeliest thing to export (§1.3), so the
        // natural spelling has to work; `on <planes> export <decl>` also parses,
        // via the EXPORT branch inside `parseDeclaration`.
        if (this.check("EXPORT") && this.peekAhead(1)?.type === "ON") {
          this.advance();
          this.parseOnPresence(declarations, true);
          continue;
        }
        const decl = this.parseDeclaration();
        if (decl) declarations.push(decl);
      } catch (_e) {
        this.synchronizeToToplevel();
      }
    }

    const lastTok = this.pos > 0 ? this.tokens[this.pos - 1] : undefined;
    const endSpan = lastTok?.span ?? startSpan;
    return {
      file: {
        declarations,
        uses,
        ...(this.onInclusions.length ? { onInclusions: this.onInclusions } : {}),
        source: { start: startSpan.start, end: endSpan.end, file: startSpan.file },
      },
      errors: this.errors,
    };
  }

  /**
   * Parse a `package.archspace` manifest. Different top-level grammar:
   *   - `name: <dotted>`                            (required, exactly once)
   *   - `version: "<string>"`                       (optional, exactly once)
   *   - `widgets: "<relative path>"`                (optional, exactly once)
   *   - `"<bare string>"`                           (zero or more; package description)
   *   - `dependencies { <dotted>: "<path>", ... }`  (zero or one block)
   *   - `use ... from <pkg>` / `export use ...`     (zero or more)
   * No type/module/process/view declarations allowed. Duplicates are
   * reported as errors; the first occurrence wins so downstream tooling
   * has something to work with.
   */
  parseManifest(): {
    manifest: PackageManifest;
    errors: readonly ParseError[];
  } {
    const startTok = this.peek();
    const startSpan = startTok.span;

    let pkg: QualifiedName | undefined;
    let pkgSource: SourceSpan | undefined;
    let spaceName: QualifiedName | undefined;
    let spaceNameSource: SourceSpan | undefined;
    let version: string | undefined;
    let versionSource: SourceSpan | undefined;
    let widgets: string | undefined;
    let widgetsSource: SourceSpan | undefined;
    let dependencies: DependencyEntry[] = [];
    let dependenciesSeen = false;
    const descriptions: DescriptionEntry[] = [];
    const uses: UseDecl[] = [];

    while (!this.isAtEnd()) {
      try {
        if (this.check("USE") || (this.check("EXPORT") && this.peekAhead(1)?.type === "USE")) {
          uses.push(this.parseUseDecl());
          continue;
        }
        // Bare description string(s) at the manifest root — same as a module body.
        if (this.check("STRING")) {
          const tok = this.advance();
          descriptions.push({ text: tok.value, source: tok.span });
          continue;
        }
        if (this.check("IDENTIFIER")) {
          const ident = this.peek();
          if (ident.value === "package" && this.peekAhead(1)?.type === "COLON") {
            this.advance(); // consume 'package'
            this.consume("COLON", "parse.expectedAfterGot", { expected: "':'", after: "'package'" });
            const path = this.parseDottedName();
            if (pkg !== undefined) {
              this.error(ident, "parse.duplicateManifest", { what: "'package'" });
            } else {
              pkg = path;
              pkgSource = ident.span;
            }
            continue;
          }
          if (ident.value === "name" && this.peekAhead(1)?.type === "COLON") {
            this.advance(); // consume 'name'
            this.consume("COLON", "parse.expectedAfterGot", { expected: "':'", after: "'name'" });
            const path = this.parseDottedName();
            if (spaceName !== undefined) {
              this.error(ident, "parse.duplicateManifest", { what: "'name'" });
            } else {
              spaceName = path;
              spaceNameSource = ident.span;
            }
            continue;
          }
          if (ident.value === "version" && this.peekAhead(1)?.type === "COLON") {
            this.advance(); // consume 'version'
            this.consume("COLON", "parse.expectedAfterGot", { expected: "':'", after: "'version'" });
            const valTok = this.consume("STRING", "parse.expectedThingGot", { thing: 'version string' });
            if (version !== undefined) {
              this.error(ident, "parse.duplicateManifest", { what: "'version'" });
            } else {
              version = valTok.value;
              versionSource = ident.span;
            }
            continue;
          }
          if (ident.value === "widgets" && this.peekAhead(1)?.type === "COLON") {
            this.advance(); // consume 'widgets'
            this.consume("COLON", "parse.expectedAfterGot", { expected: "':'", after: "'widgets'" });
            const valTok = this.consume("STRING", "parse.expectedThingGot", { thing: 'widgets path string' });
            if (widgets !== undefined) {
              this.error(ident, "parse.duplicateManifest", { what: "'widgets'" });
            } else {
              widgets = valTok.value;
              widgetsSource = ident.span;
            }
            continue;
          }
          if (ident.value === "dependencies" && this.peekAhead(1)?.type === "LBRACE") {
            const block = this.parseDependenciesBlock();
            if (dependenciesSeen) {
              this.error(ident, "parse.duplicateManifest", { what: "'dependencies' block" });
            } else {
              dependencies = block;
              dependenciesSeen = true;
            }
            continue;
          }
        }
        this.error(this.peek(), "parse.unexpectedTokenIn", { context: "package manifest", value: this.peek().value });
        this.advance();
      } catch (_e) {
        this.synchronizeToToplevel();
      }
    }

    if (pkg !== undefined && spaceName !== undefined) {
      this.error(startTok, "parse.manifestBothPackageAndName");
    } else if (pkg === undefined && spaceName === undefined) {
      this.error(startTok, "parse.manifestNeitherPackageNorName");
      pkg = ["__anonymous__"];
    }

    const lastTok = this.pos > 0 ? this.tokens[this.pos - 1] : undefined;
    const endSpan = lastTok?.span ?? startSpan;
    return {
      manifest: {
        package: pkg,
        packageSource: pkgSource,
        spaceName,
        spaceNameSource,
        version,
        versionSource,
        widgets,
        widgetsSource,
        descriptions,
        dependencies,
        uses,
        source: { start: startSpan.start, end: endSpan.end, file: startSpan.file },
      },
      errors: this.errors,
    };
  }


  private parseDeclaration(): TopLevelDecl | null {
    // Decorations are the OUTERMOST prefix: `@deco* [export] [on …|in …] decl`.
    // Buffer them, parse the declaration they sit above, then stamp — the same
    // shape `on <planes> <decl>` uses, except decorations stack.
    if (this.check("AT")) {
      const at = this.peek();
      const decorations = this.parseDecorationPrefix();
      if (this.check("DECORATION")) {
        this.error(at, "parse.decorationOnDecoration");
        return { kind: "decoration", decl: this.parseDecorationDecl() };
      }
      const inner = this.parseDeclaration();
      return this.stampDecorations(inner, decorations, at.span);
    }
    if (this.check("DECORATION")) {
      return { kind: "decoration", decl: this.parseDecorationDecl() };
    }
    // `export type X { ... }` — exported type declaration
    if (this.check("EXPORT") && this.peekAhead(1)?.type === "TYPE") {
      const exportTok = this.advance();
      const decl = this.parseTypeDecl();
      return { kind: "type", decl: { ...decl, exported: true, source: decl.source ? { start: exportTok.span.start, end: decl.source.end, file: decl.source.file } : decl.source } };
    }
    // `export subprocess X(...) { ... }` — published across the space boundary.
    if (this.check("EXPORT") && this.peekAhead(1)?.type === "SUBPROCESS") {
      const exportTok = this.advance();
      const decl = this.parseSubprocessDecl();
      const widened = decl.source ? { start: exportTok.span.start, end: decl.source.end, file: decl.source.file } : decl.source;
      return { kind: "subprocess", decl: { ...decl, exported: true, source: widened, extentSource: widened } };
    }
    // `export process X { ... }` — a sanctioned cross-space `do` entry point
    // (§4.11, v0.10.1). Top-level only; the module-body / `in`-placement paths
    // reject `export` with a targeted message. Mirrors `export subprocess`.
    if (this.check("EXPORT") && this.peekAhead(1)?.type === "PROCESS") {
      const exportTok = this.advance();
      const decl = this.parseProcessDecl();
      const widened = decl.source ? { start: exportTok.span.start, end: decl.source.end, file: decl.source.file } : decl.source;
      return { kind: "process", decl: { ...decl, exported: true, source: widened, extentSource: widened } };
    }
    // v0.10: `export query` / `export policy` — published governance/vocabulary.
    if (this.check("EXPORT") && this.isV010DeclHeadAt(1, "query")) {
      this.advance();
      return { kind: "query", decl: this.parseV010Query(true) };
    }
    if (this.check("EXPORT") && this.isV010DeclHeadAt(1, "policy")) {
      this.advance();
      return { kind: "policy", decl: this.parseV010Policy(true) };
    }
    // `export <kind> X { … }` — an exported MODULE (§4.11). On a module,
    // `export` sanctions being named as a PLACE from another space (§1.3);
    // calls into it are still governed by the interfaces' own `export`.
    if (this.check("EXPORT") && this.peekAhead(1)?.type === "IDENTIFIER") {
      const exportTok = this.advance();
      const decl = this.parseModuleDecl();
      const widened = decl.source
        ? { start: exportTok.span.start, end: decl.source.end, file: decl.source.file }
        : decl.source;
      return { kind: "module", decl: { ...decl, exported: true, extentSource: widened } };
    }
    if (this.check("TYPE")) {
      return { kind: "type", decl: this.parseTypeDecl() };
    }
    if (this.check("PROCESS")) {
      return { kind: "process", decl: this.parseProcessDecl() };
    }
    if (this.check("SUBPROCESS")) {
      return { kind: "subprocess", decl: this.parseSubprocessDecl() };
    }
    if (this.check("VIEW")) {
      return { kind: "view", decl: this.parseV010View() };
    }
    // `in <Target> …` placement / extension (§4.16). Leads with the `in`
    // keyword, so it is unambiguous against a module_decl (which leads with a
    // type identifier).
    if (this.check("IN")) {
      return this.parseInPlacement();
    }
    // Process-tests: `test "…" [of P] { … }` at top level (§4). Checked BEFORE
    // the bare-step branch: `test "name"` is `IDENT STRING`, which
    // `isBareStepAhead` would otherwise read as a call-with-comment step.
    if (this.isTestHeadAt(0)) {
      return { kind: "test", decl: this.parseV010Test() };
    }
    // Bare step(s) written at top level → an anonymous process (§4.13).
    if (this.isBareStepAhead()) {
      const decl = this.parseAnonymousProcess();
      return decl ? { kind: "process", decl } : null;
    }
    // v0.10 governance/query declarations (contextual keyword at member head;
    // `query Name:` / `policy Name {` — a `:`/`.` after the head means a field).
    if (this.isV010DeclHeadAt(0, "query")) {
      return { kind: "query", decl: this.parseV010Query(false) };
    }
    if (this.isV010DeclHeadAt(0, "policy")) {
      return { kind: "policy", decl: this.parseV010Policy(false) };
    }
    // `style bundle <Name> {` → bundle decl; `lens {` → lens decl (member-head
    // dispatch, §5.2). A `style`/`lens` head followed by `:`/`.` is a field.
    if (this.isIdentValue(0, "style") && this.isIdentValue(1, "bundle")) {
      return { kind: "styleBundle", decl: this.parseV010StyleBundle() };
    }
    if (this.isIdentValue(0, "lens") && this.peekAhead(1)?.type === "LBRACE") {
      return { kind: "lens", decl: this.parseV010Lens() };
    }
    if (this.check("IDENTIFIER")) {
      return { kind: "module", decl: this.parseModuleDecl() };
    }

    this.error(this.peek(), "parse.unexpectedTokenIn", { context: "top level", value: this.peek().value });
    this.advance();
    return null;
  }

  /**
   * v0.9.0 §1.2 — `on <planes> …` born-on presence (top level).
   *   `on deployment module Euro { … }`   one stamped declaration
   *   `on deployment { …decls…  Ref }`    many; bare qualified names are
   *                                       INCLUSIONS (add the plane to an
   *                                       existing module's plane set)
   * Stamped declarations exist on the listed planes ONLY (primary is not
   * implied — that is the point). Modules, processes, and subprocesses can be
   * plane-scoped; types/views/placements cannot.
   */
  private parseOnPresence(declarations: TopLevelDecl[], exported = false): void {
    const onTok = this.consume("ON", "parse.expectedThingGot", { thing: "'on'" });
    const planes = this.parsePlaneList();
    if (planes.length === 0) {
      this.error(onTok, "parse.expectedPlaneAfterOn");
      return;
    }
    const stamp = (decl: TopLevelDecl | null, withExtent = false): void => {
      if (!decl) return;
      if (decl.kind === "module" || decl.kind === "process" || decl.kind === "subprocess") {
        // Single-form `on <planes> <decl>` records the full prefixed extent;
        // block-form members share the header, so they get none.
        const inner = decl.decl as { source?: SourceSpan; extentSource?: SourceSpan };
        const end = (inner.extentSource ?? inner.source)?.end;
        const extentSource =
          withExtent && end ? { start: onTok.span.start, end, file: onTok.span.file } : inner.extentSource;
        declarations.push({
          ...decl,
          decl: {
            ...decl.decl,
            onPlanes: planes,
            extentSource,
            // `export` ahead of the prefix exports what the prefix stamps; only
            // a module carries the flag (§4.11).
            ...(exported && decl.kind === "module" ? { exported: true } : {}),
          },
        } as TopLevelDecl);
        return;
      }
      this.error(onTok, "parse.declCannotBePlaneScoped", { kind: decl.kind });
      declarations.push(decl);
    };
    if (this.check("LBRACE")) {
      this.advance();
      while (!this.isAtEnd() && !this.check("RBRACE")) {
        if (this.matchToken("SEMICOLON")) continue;
        // A bare (possibly dotted) name with no declaration tokens after it on
        // the same line = an inclusion reference. (A bare single segment that
        // is a registered TYPE is reclassified by the resolver, §1.2.)
        if (this.check("IDENTIFIER") && this.isBareInclusionRef()) {
          const tStart = this.peek().span;
          const target = this.parseQualifiedName();
          this.onInclusions.push({
            planes,
            target,
            source: { start: tStart.start, end: this.previous().span.end, file: tStart.file },
          });
          continue;
        }
        const before = this.pos;
        stamp(this.parseDeclaration());
        if (this.pos === before) this.advance();
      }
      this.consume("RBRACE", "parse.expectedCloseToGot", { what: "'on' block" });
      return;
    }
    const decl = this.parseDeclaration();
    if (decl) stamp(decl, true);
    else this.error(this.peek(), "parse.expectedDeclOrOpenAfterOn", { open: OPEN_BRACE });
  }

  /** A one-line, comma-separated list of (dotted) plane names. `primary` is an
   *  ordinary name here (the implicit plane's address in this slot). */
  private parsePlaneList(): QualifiedName[] {
    const planes: QualifiedName[] = [];
    for (;;) {
      if (!this.check("IDENTIFIER")) {
        if (planes.length === 0) this.error(this.peek(), "parse.expectedPlaneNameOn");
        break;
      }
      planes.push(this.parseQualifiedName());
      if (!this.check("COMMA") || this.lineBreakBetween(this.previous(), this.peek())) break;
      const comma = this.advance();
      if (!this.check("IDENTIFIER") || this.lineBreakBetween(comma, this.peek())) {
        this.error(comma, "parse.planeListOneLine");
        break;
      }
    }
    return planes;
  }

  /** Lookahead: the (dotted) name at the cursor is a bare inclusion reference —
   *  nothing declaration-like OR step-like follows it on the same line. */
  private isBareInclusionRef(): boolean {
    const after = this.tokenAfterQName();
    if (!after) return true;
    // `A > …` / `A = …` / `A "note"` — the name is a step CALLER (or bound-result
    // name / note actor), not an inclusion. Let it fall through to a bare step.
    if (after.type === "GT" || after.type === "EQUALS" || after.type === "STRING") return false;
    if (after.type !== "IDENTIFIER" && after.type !== "STABLE_ID" && after.type !== "LBRACE") return true;
    // Declaration tokens only count on the SAME line (`A\nsvc B {}` = ref + decl).
    const saved = this.pos;
    // walk to the end of the qualified name to compare lines
    let i = this.pos;
    while (this.tokens[i + 1]?.type === "DOT" && this.tokens[i + 2]?.type === "IDENTIFIER") i += 2;
    const lastNameTok = this.tokens[i];
    this.pos = saved;
    return lastNameTok != null && this.lineBreakBetween(lastNameTok, after);
  }

  /**
   * `in <Target> …` — unified placement / extension (§4.16, v0.8.2).
   *   `in Target <decl>`  places one declaration into Target (parentPath).
   *   `in Target { … }`   reopens Target and merges the body (ExtensionDecl).
   * Disambiguated by the token after the target: `{` → block; else single decl.
   */
  private parseInPlacement(): TopLevelDecl | null {
    const inTok = this.consume("IN", "parse.expectedThingGot", { thing: "'in'" });
    const targetStart = this.peek().span;
    const target = this.parseQualifiedName();
    const targetSource: SourceSpan = {
      start: targetStart.start,
      end: this.previous().span.end,
      file: targetStart.file,
    };

    if (this.check("LBRACE")) {
      const body = this.parseBody();
      return {
        kind: "extension",
        decl: {
          target,
          body,
          targetSource,
          source: { start: inTok.span.start, end: this.previous().span.end, file: inTok.span.file },
        },
      };
    }

    // Single form: `in Target <decl>`. A placed decl is nested in Target, so it
    // is NAME-OPTIONAL (name-from-type, §4.14) — route modules through the
    // body-style `parseSubDecl`, NOT the top-level name-required `parseModuleDecl`.
    const conflictIfInClause = (parentPath: QualifiedName | undefined): void => {
      if (parentPath && parentPath.length > 0) {
        this.error(inTok, "parse.conflictingPlacementInClause");
      }
    };

    // Full prefixed extent — `source` stays on the decl itself (incremental
    // slicing depends on that); delete/move consume `extentSource` instead.
    const extentFrom = (declSource: SourceSpan | undefined): SourceSpan | undefined =>
      declSource ? { start: inTok.span.start, end: declSource.end, file: inTok.span.file } : undefined;

    // `in <Target> export process …` — `export` publishes a TOP-LEVEL process's
    // cross-space surface; an `in`-placed process is scoped under a module and
    // takes no `export` (§4.11, GRAMMAR §Process Declarations). Recover by
    // parsing the placed decl un-exported so the model stays renderable.
    if (this.check("EXPORT") && this.peekAhead(1)?.type === "PROCESS") {
      const exp = this.advance();
      this.error(exp, "parse.exportInPlacedProcess");
      const decl = this.parseProcessDecl();
      conflictIfInClause(decl.parentPath);
      return {
        kind: "process",
        decl: { ...decl, parentPath: target, parentSource: targetSource, extentSource: extentFrom(decl.source) },
      };
    }
    if (this.check("PROCESS")) {
      const decl = this.parseProcessDecl();
      conflictIfInClause(decl.parentPath);
      return {
        kind: "process",
        decl: { ...decl, parentPath: target, parentSource: targetSource, extentSource: extentFrom(decl.source) },
      };
    }
    // v0.10 §5.2 placed_decl: `in <Module> view X { }` / `in <Module> policy Y { }`
    // — the placed twins of the module-body forms. The resolver turns the target
    // into `ownerPath` (scoped name resolution / subtree scope).
    if (this.check("VIEW")) {
      const decl = this.parseV010View();
      return {
        kind: "view",
        decl: { ...decl, parentPath: target, parentSource: targetSource, extentSource: extentFrom(decl.source) },
      };
    }
    if (this.isV010DeclHeadAt(0, "policy")) {
      const decl = this.parseV010Policy(false);
      return {
        kind: "policy",
        decl: { ...decl, parentPath: target, parentSource: targetSource, extentSource: extentFrom(decl.source) },
      };
    }
    // v0.10 §5.2 placed twin: `in <Module> test "…" { … }` (process-tests §4).
    if (this.isTestHeadAt(0)) {
      const decl = this.parseV010Test();
      return {
        kind: "test",
        decl: { ...decl, parentPath: target, parentSource: targetSource, extentSource: extentFrom(decl.source) },
      };
    }
    // `query`/`lens` are NOT placeable (§5.2: placed_decl is view/policy only) —
    // they declare at file top level or inside the module body. Consume the
    // declaration so parsing recovers cleanly past it.
    if (this.isV010DeclHeadAt(0, "query")) {
      this.error(inTok, "parse.inCannotPlaceQuery");
      this.parseV010Query(false);
      return null;
    }
    if (this.isIdentValue(0, "lens") && this.peekAhead(1)?.type === "LBRACE") {
      this.error(inTok, "parse.inCannotPlaceLens");
      this.parseV010Lens();
      return null;
    }
    if (this.check("IDENTIFIER")) {
      const member = this.parseSubDecl();
      if (member?.kind === "module") {
        conflictIfInClause(member.decl.parentPath);
        return {
          kind: "module",
          decl: { ...member.decl, parentPath: target, parentSource: targetSource, extentSource: extentFrom(member.decl.source) },
        };
      }
      this.error(inTok, "parse.inPlaceableKinds");
      return null;
    }
    this.error(inTok, "parse.inExpectsDeclOrBlock", { frag: "'{ … }'" });
    return null;
  }

  // -------------------------------------------------------------------------
  // Decorations — `decoration Name { … }` and the `@name(args)` line prefix
  // -------------------------------------------------------------------------

  /**
   * `decoration Name { targets: …; field: type … }`.
   *
   * `targets` is a RESERVED meta-field here, never a schema field — validation
   * rejects a schema field named `targets` so the two can never be confused.
   */
  private parseDecorationDecl(): DecorationDecl {
    const start = this.consume("DECORATION", "parse.expectedThingGot", { thing: "'decoration'" });
    const nameTok = this.consume("IDENTIFIER", "parse.expectedThingGot", {
      thing: "decoration name",
    });
    const fields: DecorationField[] = [];
    const descriptions: string[] = [];
    let targets: Name[] | undefined;
    let targetsSource: SourceSpan | undefined;

    if (this.check("LBRACE")) {
      this.advance();
      let prevMemberEnd: Token | null = null;
      while (!this.isAtEnd() && !this.check("RBRACE")) {
        if (prevMemberEnd) {
          const sawSep = this.consumeMemberSeparators("members");
          if (!sawSep && !this.lineBreakBetween(prevMemberEnd, this.peek())) {
            this.error(this.peek(), "parse.sepBetweenNewline", { noun: "members" });
          }
        }
        if (this.check("RBRACE")) break;
        const before = this.pos;
        if (this.check("STRING")) {
          const s = this.advance();
          descriptions.push(s.value);
        } else if (this.isIdentValue(0, "targets") && this.peekN(1)?.type === "COLON") {
          const t = this.advance();
          this.advance(); // ':'
          const list: Name[] = [];
          do {
            const k = this.decorationNameToken();
            if (!k) {
              this.error(this.peek(), "parse.expectedThingGot", { thing: "a target kind" });
              break;
            }
            list.push(k.value);
          } while (this.matchToken("COMMA"));
          targets = list;
          targetsSource = { start: t.span.start, end: this.previous().span.end, file: t.span.file };
        } else if (this.peekN(1)?.type === "COLON" && this.decorationFieldHeadHere()) {
          const f = this.parseDecorationField();
          if (f) fields.push(f);
        } else {
          this.error(this.peek(), "parse.expectedThingGot", { thing: "a decoration schema field" });
        }
        if (this.pos === before) this.advance();
        prevMemberEnd = this.previous();
      }
      this.consume("RBRACE", "parse.expectedThingGot", { thing: "'}'" });
    }

    const end = this.previous().span;
    return {
      name: nameTok.value,
      targets,
      fields,
      descriptions: descriptions.length > 0 ? descriptions : undefined,
      source: { start: start.span.start, end: end.end, file: start.span.file },
      nameSource: nameTok.span,
      targetsSource,
    };
  }

  /**
   * A name slot inside a decoration — schema field, arg name, or target kind.
   *
   * Args ARE fields (§4.3) and fields may carry keyword names, so these slots
   * must accept a reserved keyword too. Without this `@deprecated(from: v2)`
   * and `targets: type, use, process` both fail to parse — `from`, `type`,
   * `use` and `process` are all keyword tokens, not IDENTIFIERs.
   */
  private decorationNameToken(): Token | null {
    const t = this.peek();
    if (t.type === "IDENTIFIER" || RESERVED_KEYWORDS.has(t.value)) return this.advance();
    return null;
  }

  /**
   * Attach buffered decorations to the declaration they prefixed, widening
   * `extentSource` to the first `@`. `source` is deliberately left alone —
   * incremental slicing keys on it, exactly as for the `export` / `on` prefixes.
   *
   * View-layer artifacts (`view` / `policy` / `query` / `test` / `lens` /
   * `styleBundle`) cannot carry a decoration; they are rejected here rather
   * than silently dropped. They may still READ one in a selector.
   */
  private stampDecorations(
    inner: TopLevelDecl | null,
    decorations: readonly DecorationInstance[],
    prefixStart: SourceSpan,
  ): TopLevelDecl | null {
    if (!inner || decorations.length === 0) return inner;
    const widen = (s: SourceSpan | undefined): SourceSpan | undefined =>
      s ? { start: prefixStart.start, end: s.end, file: s.file } : s;
    switch (inner.kind) {
      // A type has no `extentSource` — a decorated type keeps its own span.
      case "type":
        return { ...inner, decl: { ...inner.decl, decorations } };
      case "module":
      case "process":
      case "subprocess":
        return {
          ...inner,
          decl: {
            ...inner.decl,
            decorations,
            extentSource: widen(inner.decl.extentSource ?? inner.decl.source),
          },
        } as TopLevelDecl;
      default:
        this.error(this.previous(), "parse.decorationNotAttachable", {
          kind: inner.kind,
          name: decorations[0]?.name ?? "",
        });
        return inner;
    }
  }

  private decorationFieldHeadHere(): boolean {
    const t = this.peek();
    return t.type === "IDENTIFIER" || RESERVED_KEYWORDS.has(t.value);
  }

  /** Lookahead past a stack of `@name(args)` lines to see whether a `use`
   *  import follows — `use` is collected separately from declarations, so the
   *  dispatcher has to know before it commits to a branch. */
  /** Index of the first token PAST a run of `@deco(...)` prefixes. */
  private indexAfterDecorations(): number {
    let i = 0;
    for (;;) {
      if (this.peekAhead(i)?.type !== "AT") break;
      i += 2; // '@' + name
      if (this.peekAhead(i)?.type === "LPAREN") {
        let depth = 0;
        for (; this.peekAhead(i); i++) {
          const t = this.peekAhead(i)?.type;
          if (t === "LPAREN") depth++;
          else if (t === "RPAREN" && --depth === 0) {
            i++;
            break;
          }
        }
      }
    }
    return i;
  }

  /** Whether `head` (optionally behind `export`) follows the decoration run. */
  private headAfterDecorations(head: TokenType): boolean {
    const i = this.indexAfterDecorations();
    const t = this.peekAhead(i)?.type;
    return t === head || (t === "EXPORT" && this.peekAhead(i + 1)?.type === head);
  }

  private useAheadOfDecorations(): boolean {
    return this.headAfterDecorations("USE");
  }

  private onAheadOfDecorations(): boolean {
    return this.headAfterDecorations("ON");
  }

  /**
   * An argument value. `parseValue` stops at a single identifier, so a dotted
   * element reference (`use: Billing.V2`) is assembled here into a `ref` — the
   * same shape an aspect membership value takes.
   */
  private parseDecorationArgValue(): Value {
    const start = this.peek();
    const v = this.parseValue();
    // A dotted chain whose next segment is a NUMBER is a version marker, not a
    // qualified ref: `1.5.2`, `v26.17.2`, `2026.1`. Glue it back into the text
    // the author wrote — otherwise `.` reads as element qualification and the
    // exact spellings a roadmap is written in would all need quoting. A ref
    // (`use: Billing.V2`) has an IDENTIFIER after the dot and takes the branch
    // below, so the two never compete.
    if ((v.kind === "identifier" || v.kind === "number") && this.check("DOT") && this.peekN(1)?.type === "NUMBER") {
      let text = v.kind === "identifier" ? v.text : start.value;
      while (this.check("DOT") && this.peekN(1)?.type === "NUMBER") {
        this.advance(); // '.'
        text += "." + this.advance().value;
      }
      return {
        kind: "string",
        text,
        source: { start: start.span.start, end: this.previous().span.end, file: start.span.file },
      };
    }
    // A number whose spelling carries more than its value — `1.10`, `2.0` — is a
    // marker, not a quantity: `String(1.10)` is `"1.1"`, which would sort the
    // tenth patch ahead of the second and collapse it onto `1.1`.
    if (v.kind === "number" && String(v.value) !== start.value) {
      return { kind: "string", text: start.value, source: v.source };
    }
    if (v.kind !== "identifier" || !this.check("DOT")) return v;
    const path: Name[] = [v.text];
    while (this.matchToken("DOT")) {
      const seg = this.decorationNameToken();
      if (!seg) {
        this.error(this.peek(), "parse.expectedThingGot", { thing: "identifier after '.'" });
        break;
      }
      path.push(seg.value);
    }
    return {
      kind: "ref",
      path,
      source: { start: start.span.start, end: this.previous().span.end, file: start.span.file },
    };
  }

  /** `name: <type>` where type is one of the tiny decoration-local set. */
  private parseDecorationField(): DecorationField | null {
    const nameTok = this.advance();
    if (!this.check("COLON")) {
      this.error(nameTok, "parse.expectedThingGot", { thing: "':' after a schema field name" });
      return null;
    }
    this.advance(); // ':'
    const typeTok = this.consume("IDENTIFIER", "parse.expectedThingGot", {
      thing: "a field type (marker, text, number, bool, ref, list)",
    });
    let of: DecorationFieldType | undefined;
    if (typeTok.value === "list" && this.isIdentValue(0, "of")) {
      this.advance(); // 'of'
      const elem = this.consume("IDENTIFIER", "parse.expectedThingGot", {
        thing: "an element type after 'list of'",
      });
      of = asDecorationFieldType(elem.value);
      if (!of) this.error(elem, "parse.decorationBadFieldType", { got: elem.value });
    }
    const type = asDecorationFieldType(typeTok.value);
    if (!type) {
      this.error(typeTok, "parse.decorationBadFieldType", { got: typeTok.value });
      return null;
    }
    return {
      name: nameTok.value,
      type,
      of,
      source: { start: nameTok.span.start, end: this.previous().span.end, file: nameTok.span.file },
      nameSource: nameTok.span,
    };
  }

  /**
   * Buffer the `@name(args)` lines stacked above a declaration.
   *
   * Decorations are the OUTERMOST prefix — `@deco* [export] [on … | in …] decl`
   * — so this runs before every other prefix check. Disambiguation from an
   * expression-position `@getter` is positional and total: decorations parse
   * only where a declaration or body member is expected, and no step, field
   * value, or body-member dispatch can begin with `AT`.
   */
  private parseDecorationPrefix(): DecorationInstance[] {
    const out: DecorationInstance[] = [];
    while (this.check("AT")) {
      const at = this.advance();
      const nameTok = this.consume("IDENTIFIER", "parse.expectedThingGot", {
        thing: "a decoration name after '@'",
      });
      const args: DecorationArg[] = [];
      let bare = true;
      if (this.check("LPAREN")) {
        this.advance();
        bare = false;
        if (!this.check("RPAREN")) {
          do {
            const argStart = this.peek();
            // `name: value` — the name slot takes a keyword too (`from:`, `use:`).
            if (this.peekN(1)?.type === "COLON" && this.decorationFieldHeadHere()) {
              const n = this.advance();
              this.advance(); // ':'
              const value = this.parseDecorationArgValue();
              args.push({
                name: n.value,
                value,
                source: {
                  start: n.span.start,
                  end: this.previous().span.end,
                  file: n.span.file,
                },
                nameSource: n.span,
              });
            } else {
              const value = this.parseDecorationArgValue();
              args.push({
                value,
                source: {
                  start: argStart.span.start,
                  end: this.previous().span.end,
                  file: argStart.span.file,
                },
              });
            }
          } while (this.matchToken("COMMA"));
        }
        this.consume("RPAREN", "parse.expectedThingGot", { thing: "')'" });
        // `@deco()` is identical to bare `@deco`; the formatter normalises it.
        if (args.length === 0) bare = true;
      }
      // 2+ args must ALL be named — no positional/named mixing.
      if (args.length > 1 && args.some((a) => a.name === undefined)) {
        this.error(nameTok, "parse.decorationMixedArgs", { name: nameTok.value });
      }
      out.push({
        name: nameTok.value,
        args,
        bare: bare || undefined,
        source: { start: at.span.start, end: this.previous().span.end, file: at.span.file },
        nameSource: nameTok.span,
      });
    }
    return out;
  }

  // -------------------------------------------------------------------------
  // Use / export-use declarations
  // -------------------------------------------------------------------------

  private parseUseDecl(): UseDecl {
    let reExport = false;
    let start = this.peek().span;
    if (this.check("EXPORT")) {
      const exp = this.advance();
      start = exp.span;
      reExport = true;
    }
    this.consume("USE", "parse.expectedThingGot", { thing: "'use'" });

    let wildcard = false;
    const imports: UseImport[] = [];

    if (this.check("STAR")) {
      this.advance();
      wildcard = true;
    } else {
      // One or more `Name (as Alias)?`, comma-separated.
      do {
        const nameTok = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: "import name after 'use'" });
        let alias: Name | undefined;
        let aliasSource: SourceSpan | undefined;
        if (this.check("AS")) {
          this.advance(); // consume 'as'
          const aliasTok = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: "alias after 'as'" });
          alias = aliasTok.value;
          aliasSource = aliasTok.span;
        }
        imports.push({
          name: nameTok.value,
          alias,
          source: alias && aliasSource
            ? { start: nameTok.span.start, end: aliasSource.end, file: nameTok.span.file }
            : nameTok.span,
          nameSource: nameTok.span,
          aliasSource,
        });
      } while (this.matchToken("COMMA"));
    }

    this.consume("FROM", "parse.expectedThingGot", { thing: "'from' in use declaration" });
    const fromStart = this.peek().span;
    const fromPackage = this.parseDottedName();
    const fromSource: SourceSpan = {
      start: fromStart.start,
      end: this.previous().span.end,
      file: fromStart.file,
    };
    const end = this.previous().span;

    return {
      fromPackage,
      wildcard,
      imports,
      reExport,
      source: { start: start.start, end: end.end, file: start.file },
      fromSource,
    };
  }

  private parseDependenciesBlock(): DependencyEntry[] {
    const blockTok = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: "'dependencies'" });
    this.consume("LBRACE", "parse.expectedOpenAfterGot", { after: "'dependencies'" });
    const out: DependencyEntry[] = [];
    const seen = new Set<string>();
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      // <dotted>: "<path>"
      const startSpan = this.peek().span;
      const namePath = this.parseDottedName();
      const nameSource: SourceSpan = {
        start: startSpan.start,
        end: this.previous().span.end,
        file: startSpan.file,
      };
      this.consume("COLON", "parse.expectedAfterGot", { expected: "':'", after: 'dependency name' });
      const pathTok = this.consume("STRING", "parse.expectedThingGot", { thing: 'dependency path string' });
      const joined = namePath.join(".");
      if (seen.has(joined)) {
        this.error(blockTok, "parse.duplicateDependency", { name: joined });
      } else {
        seen.add(joined);
        out.push({
          name: namePath,
          path: pathTok.value,
          source: { start: startSpan.start, end: pathTok.span.end, file: startSpan.file },
          nameSource,
          pathSource: pathTok.span,
        });
      }
      // Same separation rule as every other body: ';' or a newline.
      const sawSep = this.consumeMemberSeparators("entries");
      if (this.check("RBRACE") || this.isAtEnd()) break;
      if (!sawSep && !this.lineBreakBetween(this.previous(), this.peek())) {
        this.error(this.peek(), "parse.sepBetweenNewline", { noun: 'entries' });
      }
    }
    this.consume("RBRACE", "parse.expectedCloseToGot", { what: "'dependencies'" });
    return out;
  }

  /** A path segment after a `.` may be an identifier OR a reserved keyword used
   *  as a member name (`Worker.process`, `Gateway.select`). A keyword is
   *  unambiguous in member position (it never starts a step there), so the
   *  process language's reserved words don't block ordinary domain interface
   *  names. The FIRST segment is never a keyword (that position IS ambiguous). */
  private checkMemberSegment(): boolean {
    return this.check("IDENTIFIER") || RESERVED_KEYWORDS.has(this.peek().value);
  }

  private parseDottedName(): QualifiedName {
    const out: Name[] = [];
    const first = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: 'identifier' });
    out.push(first.value);
    while (this.matchToken("DOT")) {
      const next = this.checkMemberSegment()
        ? this.advance()
        : this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: "identifier after '.'" });
      out.push(next.value);
    }
    return out;
  }

  private peekAhead(n: number): Token | undefined {
    return this.tokens[this.pos + n];
  }

  // -------------------------------------------------------------------------
  // Type declaration
  // -------------------------------------------------------------------------

  private parseTypeDecl(): TypeDecl {
    const start = this.consume("TYPE", "parse.expectedThingGot", { thing: "'type'" }).span;

    const idTok = this.matchToken("STABLE_ID");
    const id = idTok ? stableId(idTok.value.slice(1)) : undefined;
    const idSource = idTok?.span;

    // Parent type may be qualified (`Space.Path.Type`, v0.5) so a type can
    // subtype an exported type declared in another space.
    const parentTok = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: "parent type name after 'type'" });
    const parentParts = [parentTok.value];
    while (this.matchToken("DOT")) {
      const seg = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: "identifier after '.' in parent type" });
      parentParts.push(seg.value);
    }
    const parentType = parentParts.join(".");
    const parentEnd = this.previous().span;
    const parentTypeSource = {
      start: parentTok.span.start,
      end: parentEnd.end,
      file: parentTok.span.file,
    };

    if (parentType === "process" || parentType === "view") {
      this.error(parentTok, "parse.typeSubtypingReserved", { parentType });
    }

    const nameTok = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: 'type name' });
    const name = nameTok.value;

    const body = this.parseOptionalBody();
    const end = this.previous().span;

    return {
      id,
      parentType,
      name,
      body,
      source: { start: start.start, end: end.end, file: start.file },
      idSource,
      parentTypeSource,
      nameSource: nameTok.span,
    };
  }

  // -------------------------------------------------------------------------
  // Module declaration
  // -------------------------------------------------------------------------

  /**
   * `<kind> [#id] <name> [in <qualified-parent>] [body]`
   * Caller has already verified the leading IDENTIFIER token.
   */
  private parseModuleDecl(): ModuleDecl {
    const typeTok = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: 'type name' });
    const start = typeTok.span;
    const typeName = typeTok.value;

    const idTok = this.matchToken("STABLE_ID");
    const id = idTok ? stableId(idTok.value.slice(1)) : undefined;
    const idSource = idTok?.span;

    // A reserved keyword in name position is consumed + flagged in place (so it
    // can't leak into the next declaration as a cascade); otherwise require an
    // identifier name.
    const nameTok =
      this.keywordNameHere(idTok ?? typeTok) ??
      this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: `module name after '${typeName}'` });
    const name = nameTok.value;

    let parentPath: QualifiedName | undefined;
    let parentSource: SourceSpan | undefined;
    if (this.check("IN") && !this.lineBreakBetween(this.previous(), this.peek())) {
      // v0.8.2 §4.16: the suffix `<decl> in <Parent>` is removed — placement is
      // the `in <Parent> <decl>` prefix. Consume + flag so a stray suffix fails
      // loudly (never a silent mis-parse); the decl is left un-parented.
      const inTok = this.advance();
      this.parseQualifiedName();
      this.error(inTok, "parse.suffixInRemoved");
    }

    const hasBody = this.check("LBRACE");
    const body = this.parseOptionalBody();
    const end = this.previous().span;

    return {
      typeName,
      id,
      name,
      hasBody,
      parentPath,
      body,
      source: { start: start.start, end: end.end, file: start.file },
      typeSource: typeTok.span,
      idSource,
      nameSource: nameTok.span,
      parentSource,
    };
  }

  // -------------------------------------------------------------------------
  // Process declaration
  // -------------------------------------------------------------------------

  private parseProcessDecl(): ProcessDecl {
    const start = this.consume("PROCESS", "parse.expectedThingGot", { thing: "'process'" }).span;

    const idTok = this.matchToken("STABLE_ID");
    const id = idTok ? stableId(idTok.value.slice(1)) : undefined;
    const idSource = idTok?.span;

    // Name is optional: a `process { … }` with no name is anonymous (§4.13) —
    // a name-less group of steps that is a TODO until named.
    let name = "";
    let nameSource: SourceSpan | undefined;
    let anonymous = false;
    if (this.check("IDENTIFIER")) {
      const nameTok = this.advance();
      name = nameTok.value;
      nameSource = nameTok.span;
    } else {
      anonymous = true;
    }

    // Optional `in <ModulePath>` clause — top-level form only. The validator
    // rejects this clause when the declaration is body-nested (caller knows
    // its lexical position; we cannot from here).
    let parentPath: QualifiedName | undefined;
    let parentSource: SourceSpan | undefined;
    if (this.check("IN") && !this.lineBreakBetween(this.previous(), this.peek())) {
      // v0.8.2 §4.16: the suffix `<decl> in <Parent>` is removed — placement is
      // the `in <Parent> <decl>` prefix. Consume + flag so a stray suffix fails
      // loudly (never a silent mis-parse); the decl is left un-parented.
      const inTok = this.advance();
      this.parseQualifiedName();
      this.error(inTok, "parse.suffixInRemoved");
    }

    this.consume("LBRACE", "parse.expectedOpenAfterGot", { after: 'process name' });

    const { steps, description, fields, lateFields, aspects, subprocesses } = this.parseProcessBody();

    this.consume("RBRACE", "parse.expectedCloseAfterGot", { what: 'process body' });
    const end = this.previous().span;

    return {
      id,
      name,
      ...(anonymous ? { anonymous: true } : {}),
      steps,
      description,
      ...(fields ? { fields } : {}),
      ...(lateFields ? { lateFields } : {}),
      ...(aspects ? { aspects } : {}),
      subprocesses,
      parentPath,
      parentSource,
      source: { start: start.start, end: end.end, file: start.file },
      idSource,
      nameSource,
    };
  }

  private parseSubprocessDecl(): SubprocessDecl {
    const start = this.consume("SUBPROCESS", "parse.expectedThingGot", { thing: "'subprocess'" }).span;

    const idTok = this.matchToken("STABLE_ID");
    const id = idTok ? stableId(idTok.value.slice(1)) : undefined;
    const idSource = idTok?.span;

    const nameTok = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: 'subprocess name' });
    const name = nameTok.value;

    // Optional `on Caller` template default-caller param (Spec §5.5), before
    // the value param list.
    let onCaller: string | undefined;
    let onCallerSource: SourceSpan | undefined;
    if (this.matchToken("ON")) {
      const callerTok = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: "caller name after 'on'" });
      onCaller = callerTok.value;
      onCallerSource = callerTok.span;
    }

    const params = this.parseOptionalParamList();

    this.consume("LBRACE", "parse.expectedOpenAfterGot", { after: 'subprocess header' });

    const { steps, description, fields, lateFields, subprocesses } = this.parseProcessBody();

    this.consume("RBRACE", "parse.expectedCloseAfterGot", { what: 'subprocess body' });
    const end = this.previous().span;

    return {
      id,
      name,
      onCaller,
      onCallerSource,
      params,
      steps,
      description,
      ...(fields ? { fields } : {}),
      ...(lateFields ? { lateFields } : {}),
      subprocesses,
      source: { start: start.start, end: end.end, file: start.file },
      idSource,
      nameSource: nameTok.span,
    };
  }

  /**
   * Body shared by `process` and `subprocess`. Accepts:
   *   - free-form description strings (bare `"…"` members; v0.8 — a bare string
   *     is a *description*, never a note: a note REQUIRES an owner). Multiple
   *     concatenate with `\n` per the body-semantics rule.
   *   - any number of nested `subprocess` declarations (scoped to the
   *     enclosing block)
   *   - any number of process steps, newline- or `;`-separated
   * The categories may be interleaved; nested subprocesses are lifted out into
   * their own list while steps preserve order.
   */
  private parseProcessBody(): {
    steps: ProcessStep[];
    description?: string;
    fields?: FieldDecl[];
    lateFields?: FieldDecl[];
    aspects?: AspectEntry[];
    subprocesses: SubprocessDecl[];
  } {
    const steps: ProcessStep[] = [];
    const subprocesses: SubprocessDecl[] = [];
    let description: string | undefined;
    let fields: FieldDecl[] | undefined;
    let lateFields: FieldDecl[] | undefined;
    // v0.10 §5.1: header-zone classification aspects (`aspect team: "Ops"`).
    const aspects: AspectEntry[] = [];
    const rosters: AspectRoster[] = [];
    // v0.9.0 §2.6/§2.7: string/field meaning is positional — the HEADER zone
    // ends at the first action. Leading strings concatenate into the
    // description; later strings are flow prose. Fields are header-only
    // (later ones parse for recovery; validation rejects them).
    let sawAction = false;
    while (!this.isAtEnd() && !this.check("RBRACE")) {
      // `;` is an inline step separator — tolerate stray / leading ones.
      if (this.matchToken("SEMICOLON")) continue;
      if (this.check("STRING")) {
        const tok = this.advance();
        if (!sawAction) {
          description = description === undefined ? tok.value : `${description}\n${tok.value}`;
        } else {
          steps.push({ kind: "prose", text: tok.value, span: tok.span });
        }
        continue;
      }
      if (this.check("SUBPROCESS")) {
        subprocesses.push(this.parseSubprocessDecl());
        continue;
      }
      // v0.10 §5.1: a header-zone aspect (`aspect team: "Ops"` / `aspect { … }`).
      // Header-only, like fields — after the first action it is a diagnostic
      // (still parsed for recovery so the rest of the body survives).
      if (this.check("ASPECT")) {
        if (sawAction) {
          this.error(this.peek(), "parse.aspectsHeaderOnly");
        }
        this.parseAspectMember(aspects, rosters);
        continue;
      }
      // v0.9.0 §2.7: a field assignment (`team: "Commerce"`). Direct process
      // body only; plain/dotted identifier keys (a step never reads
      // `IDENT(.IDENT)* :`); no field bodies in the header.
      if (this.check("IDENTIFIER") && this.lookaheadIsFieldAssignment()) {
        const pathStart = this.peek().span;
        const path = this.parseQualifiedName();
        this.consume("COLON", "parse.expectedAfterGot", { expected: "':'", after: 'field name' });
        if (this.check("LBRACE")) {
          this.error(this.peek(), "parse.fieldBodiesProcess");
          this.consumeBalancedBraces();
          continue;
        }
        const value = this.parseValueList();
        const fd: FieldDecl = {
          path,
          value,
          source: { start: pathStart.start, end: this.previous().span.end, file: pathStart.file },
          pathSource: { start: pathStart.start, end: pathStart.end, file: pathStart.file },
        };
        if (!sawAction) (fields ??= []).push(fd);
        else (lateFields ??= []).push(fd);
        continue;
      }
      const before = this.pos;
      const parsed = this.parseProcessSteps();
      if (parsed.length) {
        steps.push(...parsed);
        if (parsed.some((s) => s.kind !== "prose")) sawAction = true;
      }
      // Force progress when a step parse fails without consuming a
      // token — otherwise a malformed body would loop forever on the
      // same offending token.
      else if (this.pos === before) this.advance();
      // Consume an optional inline `;` separator after the step.
      this.matchToken("SEMICOLON");
    }
    return { steps, description, fields, lateFields, aspects: aspects.length ? aspects : undefined, subprocesses };
  }

  /** Lookahead: is the token AFTER the current string (skipping inline `;`
   *  separators) the start of a bare step? (v0.9.0 §2.6 — decides
   *  prose-in-run vs description, incl. `> A ; "prose" ; > B` one-liners.) */
  private bareStepAfterString(): boolean {
    const saved = this.pos;
    this.pos++; // past the string
    while (this.check("SEMICOLON")) this.pos++;
    const yes = !this.isAtEnd() && this.isBareStepAhead();
    this.pos = saved;
    return yes;
  }

  /** Control keywords that may carry an owner prefix (`Owner if …`). */
  private isControlKeyword(t: import("./lexer.js").TokenType | undefined): boolean {
    return (
      t === "EACH" || t === "IF" || t === "SELECT" || t === "TRY" ||
      t === "PARALLEL" || t === "REVERSIBLE" || t === "AWAIT"
    );
  }

  /** A keyword that STARTS a step/construct — so it can't be read as a bare label
   *  (`branch if …` is a nested if, not a branch named "if"). Used to decide
   *  whether a keyword may serve as a parallel branch name. */
  private isStepStartKeyword(t: import("./lexer.js").TokenType | undefined): boolean {
    return (
      this.isControlKeyword(t) ||
      t === "DO" || t === "GO" || t === "FAIL" || t === "FINISH" || t === "DIST" || t === "UNWIND"
    );
  }

  /**
   * One step statement (v0.8). Dispatches on the leading token, recognising an
   * optional OWNER prefix (`Owner …` / `|` ditto / `dist`) before a call, note,
   * owner block, or control construct (Spec §5.5). Container/note steps yield
   * exactly one step; a simple step may chain (`A > B > C`) and yield several hops.
   */
  private parseProcessSteps(): ProcessStep[] {
    // `@name(args)` above a step. Only a call step (`simple`) carries them —
    // this is the site that lets `@retry` sit on a step while `@planned` is
    // rejected for it by target validation.
    if (this.check("AT")) {
      const at = this.peek();
      const decorations = this.parseDecorationPrefix();
      const steps = this.parseProcessSteps();
      const first = steps[0];
      if (first?.kind === "simple") {
        return [{ ...first, decorations }, ...steps.slice(1)];
      }
      this.error(at, "parse.decorationNotAttachable", {
        kind: first ? `${first.kind} step` : "step",
        name: decorations[0]?.name ?? "",
      });
      return steps;
    }
    // Owner-prefix forms.
    if (this.check("PIPE")) return this.parseDittoPrefixed();
    if (this.check("BACKSLASH")) return this.parseSnakePrefixed();
    if (this.check("DIST")) return this.parseDistPrefixed();

    // Control / structural keywords with no owner.
    if (this.check("EACH")) return [this.parseEachStep()];
    if (this.check("IF")) return [this.parseIfStep()];
    if (this.check("SELECT")) return [this.parseSelectStep()];
    if (this.check("TRY")) return [this.parseTryStep()];
    if (this.check("PARALLEL")) return [this.parseParallelStep()];
    if (this.check("REVERSIBLE")) return [this.parseReversibleStep()];
    if (this.check("AWAIT")) return [this.parseAwaitStep()];
    if (this.check("DO")) return [this.parseDoStep()];
    if (this.check("GO")) return [this.parseGoStep()];
    if (this.check("FAIL")) return [this.parseFailStep()];
    if (this.check("FINISH")) return [this.parseFinishStep()];

    // `unwind` is valid only inside a `reversible` item — a stray one (e.g.
    // nested inside an `if`) is an error (PROCESS.md §8: no nesting in control).
    if (this.check("UNWIND")) {
      this.error(this.peek(), "parse.unwindOnlyReversible");
      this.advance();
      // Skip the would-be unwind action to recover.
      this.parseUnwindClause();
      return [];
    }

    // Anonymous-head call: `> Callee`.
    if (this.check("GT")) return this.parseStepChain();

    // Identifier-led: owner-prefixed control / owner block / note,
    // `Caller do X`, or a call (possibly bound).
    if (this.check("IDENTIFIER")) return this.parseIdentifierLed();

    // v0.9.0 §2.6: a bare string at step level (inside a nested body) is
    // free-standing flow prose — rendered at its position, not an action.
    if (this.check("STRING")) {
      const tok = this.advance();
      return [{ kind: "prose", text: tok.value, span: tok.span }];
    }

    return this.parseStepChain(); // emits "Expected '>'"
  }

  /**
   * Identifier-led step. Scans the leading qualified name and dispatches on the
   * token that follows it: `=`/`>` → call (bound or plain); `do` → splice with a
   * template default-caller; a string → owner-anchored note; `{` → owner block;
   * a control keyword → owner-prefixed control.
   */
  private parseIdentifierLed(): ProcessStep[] {
    // `name =` single-identifier bind.
    if (this.peekAhead(1)?.type === "EQUALS") return this.parseStepChain();

    const term = this.tokenAfterQName();
    const t = term?.type;
    if (t === "GT") return this.parseStepChain();
    if (t === "STRING") return [this.parseNoteStep()];
    if (t === "DO") {
      const caller = this.parseExplicitOwner();
      return [this.parseDoStep(caller)];
    }
    if (t === "LBRACE") {
      const owner = this.parseExplicitOwner();
      return [this.parseOwnerBlock(owner)];
    }
    if (this.isControlKeyword(t)) {
      const owner = this.parseExplicitOwner();
      return [this.parseControlWithOwner(owner)];
    }
    // Falls through to a call attempt — parseStepChain reports the missing `>`.
    return this.parseStepChain();
  }

  /** `|` ditto-owner prefix before a call / note / owner block / control. */
  private parseDittoPrefixed(): ProcessStep[] {
    const pipe = this.advance(); // |
    const owner: StepOwner = { form: "ditto", source: pipe.span };
    if (this.check("GT")) return this.parseStepChain(owner);
    if (this.check("STRING")) return [this.parseNoteStep(owner)];
    if (this.check("LBRACE")) return [this.parseOwnerBlock(owner)];
    if (this.check("DO")) return [this.parseDoStep(owner)];
    if (this.isControlKeyword(this.peek().type)) {
      return [this.parseControlWithOwner(owner)];
    }
    this.error(this.peek(), "parse.expectedAfterDitto");
    return [];
  }

  /**
   * `\` snake prefix — the previous sibling step's TAIL, continuing its run
   * (§4.13.4). CALL-ONLY: it names an edge's caller, so a note, an owner block,
   * `do`, `go` or a control construct after it is `parse.snakeRequiresCall`.
   */
  private parseSnakePrefixed(): ProcessStep[] {
    const slash = this.advance(); // \
    const owner: StepOwner = { form: "snake", source: slash.span };
    if (this.check("GT")) return this.parseStepChain(owner);
    this.error(this.peek(), "parse.snakeRequiresCall");
    return [];
  }

  /** `dist` emergent-owner prefix — control-only (an owner block or control). */
  private parseDistPrefixed(): ProcessStep[] {
    const dist = this.advance(); // dist
    const owner: StepOwner = { form: "dist", source: dist.span };
    if (this.check("LBRACE")) return [this.parseOwnerBlock(owner)];
    if (this.isControlKeyword(this.peek().type)) {
      return [this.parseControlWithOwner(owner)];
    }
    this.error(dist, "parse.distControlOnly");
    return [];
  }

  /** Dispatch an owner-prefixed control construct to its parser. */
  private parseControlWithOwner(owner: StepOwner): ProcessStep {
    if (this.check("EACH")) return this.parseEachStep(owner);
    if (this.check("IF")) return this.parseIfStep(owner);
    if (this.check("SELECT")) return this.parseSelectStep(owner);
    if (this.check("TRY")) return this.parseTryStep(owner);
    if (this.check("PARALLEL")) return this.parseParallelStep(owner);
    if (this.check("REVERSIBLE")) return this.parseReversibleStep(owner);
    if (this.check("AWAIT")) return this.parseAwaitStep(owner);
    // Unreachable: caller checks isControlKeyword first.
    this.error(this.peek(), "parse.expectedControlAfterOwner");
    return this.parseFailStep();
  }

  /** Parse a qualified name as an explicit owner prefix (`Orders if …`). */
  private parseExplicitOwner(): StepOwner {
    const start = this.peek().span;
    const path = this.parseQualifiedName();
    return {
      form: "explicit",
      path,
      source: { start: start.start, end: this.previous().span.end, file: start.file },
    };
  }

  /**
   * The token immediately after a leading qualified name at the current
   * position, without consuming. Returns undefined past EOF or when the current
   * token is not the start of a qualified name.
   */
  private tokenAfterQName(): Token | undefined {
    if (this.tokens[this.pos]?.type !== "IDENTIFIER") return this.tokens[this.pos];
    let i = this.pos + 1;
    while (this.tokens[i]?.type === "DOT" && this.tokens[i + 1]?.type === "IDENTIFIER") {
      i += 2;
    }
    return this.tokens[i];
  }

  /**
   * Owner-anchored note: `Actor "…"` / `| "…"` (Spec §4.13). v0.8: the owner is
   * required — `parseProcessBody` routes a bare string to the description, so a
   * note always has an owner (an explicit name, or `|` ditto via {@link owner}).
   */
  private parseNoteStep(owner?: StepOwner): NoteStep {
    const startSpan = owner?.source ?? this.peek().span;
    let actor: QualifiedName | undefined;
    let actorSource: SourceSpan | undefined;
    let actorDitto: boolean | undefined;
    if (owner) {
      if (owner.form === "ditto") {
        actorDitto = true;
        actorSource = owner.source;
      } else {
        actor = owner.path;
        actorSource = owner.source;
      }
    } else if (this.check("IDENTIFIER")) {
      const actorStart = this.peek().span;
      actor = this.parseQualifiedName();
      actorSource = {
        start: actorStart.start,
        end: this.previous().span.end,
        file: actorStart.file,
      };
    }
    const stringTok = this.consume("STRING", "parse.expectedThingGot", { thing: 'string literal in note step' });
    // Optional `{ latency: … }` field body (latency analysis). Fields only —
    // unambiguous against an owner block, which has no preceding string.
    const fields = this.check("LBRACE") ? this.parseNoteFieldBody() : undefined;
    const end = this.previous().span;
    return {
      kind: "note",
      actor,
      ...(actorDitto ? { actorDitto: true } : {}),
      text: stringTok.value,
      ...(fields && fields.length ? { fields } : {}),
      source: { start: startSpan.start, end: end.end, file: startSpan.file },
      actorSource,
    };
  }

  /**
   * A note step's `{ … }` field body: field assignments only (`latency: 1d-3d`),
   * dotted keys allowed, no sub-declarations or nested field bodies. Anything
   * else is an error (recovered by skipping to the closing brace).
   */
  private parseNoteFieldBody(): FieldDecl[] {
    const fields: FieldDecl[] = [];
    this.consume("LBRACE", "parse.expectedOpenGot");
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      this.matchToken("SEMICOLON");
      if (this.check("RBRACE")) break;
      if (this.check("IDENTIFIER") && this.lookaheadIsFieldAssignment()) {
        const pathStart = this.peek().span;
        const path = this.parseQualifiedName();
        this.consume("COLON", "parse.expectedAfterGot", { expected: "':'", after: 'field name' });
        if (this.check("LBRACE")) {
          this.error(this.peek(), "parse.fieldBodiesNote");
          this.consumeBalancedBraces();
          continue;
        }
        const value = this.parseValueList();
        fields.push({
          path,
          value,
          source: { start: pathStart.start, end: this.previous().span.end, file: pathStart.file },
          pathSource: { start: pathStart.start, end: pathStart.end, file: pathStart.file },
        });
        continue;
      }
      this.error(this.peek(), "parse.noteBodyFieldsOnly");
      this.advance();
    }
    this.consume("RBRACE", "parse.expectedCloseToGot", { what: 'the note body' });
    return fields;
  }

  /**
   * One simple-step statement, which may chain. Shapes (§4.13):
   *   `A > B`            one hop
   *   `A > B > C`        a chain — desugars to ordered hops [A>B, B>C]; the next
   *                      hop's caller is the top module of the prior callee
   *   `> B`              anonymous head (`from === []`; the resolver fills the
   *                      enclosing module, §4.13.1)
   *   `r = A > B`        binds the chain's final hop
   *   `A > Payments.`    trailing dot ⇒ the callee interface is unspecified
   * Only hops on the same line chain; a `>` after a line break starts a fresh
   * anonymous-head statement.
   */
  private parseStepChain(headOwner?: StepOwner): SimpleStep[] {
    const startSpan = headOwner?.source ?? this.peek().span;


    // Optional `name = ` binding prefix (two-token lookahead). Binds the final
    // hop of the chain only.
    let bind: string | undefined;
    let bindSource: SourceSpan | undefined;
    if (!headOwner && this.check("IDENTIFIER") && this.peekAhead(1)?.type === "EQUALS") {
      const bindTok = this.advance();
      bind = bindTok.value;
      bindSource = bindTok.span;
      this.consume("EQUALS", "parse.expectedAfterGot", { expected: "'='", after: 'bind name' });
    }

    // A bound glyph line (`x = | > A.b`, `x = \ > A.b`): the bind comes first
    // (GRAMMAR.md `simple_step`), so the glyph is only reachable here.
    let head = headOwner;
    if (!head) {
      if (this.check("PIPE")) head = { form: "ditto", source: this.advance().span };
      else if (this.check("BACKSLASH")) head = { form: "snake", source: this.advance().span };
    }

    // Head (caller). A `|` fan head repeats the previous sibling step's HEAD and
    // a `\` snake head continues from its TAIL (both `from === []`, flagged); a
    // leading `>` is an anonymous head (from === [], no flag).
    let from: QualifiedName;
    let fromSource: SourceSpan | undefined;
    let fromDitto = false;
    let fromSnake = false;
    if (head?.form === "ditto" || head?.form === "snake") {
      from = [];
      fromDitto = head.form === "ditto";
      fromSnake = head.form === "snake";
      fromSource = head.source;
    } else if (this.check("GT")) {
      from = [];
    } else {
      const fromStart = this.peek().span;
      from = this.parseQualifiedName();
      fromSource = { start: fromStart.start, end: this.previous().span.end, file: fromStart.file };
    }

    const steps: SimpleStep[] = [];
    let hopFrom = from;
    let hopFromSource = fromSource;
    let hopFromDitto = fromDitto;
    let hopFromSnake = fromSnake;
    // The first `>` is required; later `>`s chain only when on the same line.
    while (
      this.check("GT") &&
      (steps.length === 0 || !this.lineBreakBetween(this.previous(), this.peek()))
    ) {
      this.advance(); // consume '>'
      const callee = this.parseCallTarget();
      const args = this.parseOptionalArgList();
      steps.push({
        kind: "simple",
        from: hopFrom,
        ...(hopFromDitto ? { fromDitto: true } : {}),
        ...(hopFromSnake ? { fromSnake: true } : {}),
        to: callee.to,
        ...(callee.toUnspecified ? { toUnspecified: true } : {}),
        args,
        source: { start: startSpan.start, end: this.previous().span.end, file: startSpan.file },
        fromSource: hopFromSource,
        toSource: callee.toSource,
      });
      // The next hop is performed by the top module of the callee just reached.
      hopFrom = callee.to.length > 0 ? [callee.to[0]!] : [];
      hopFromSource = callee.toSource;
      // A glyph names only the FIRST hop's caller; the rest of the chain is
      // ordinary hop-to-hop threading.
      hopFromDitto = false;
      hopFromSnake = false;
    }

    if (steps.length === 0) {
      this.error(this.peek(), "parse.expectedGtStep");
      return [];
    }

    // Optional trailing freehand comment (`> Callee "note"`), then `as name`.
    // The comment is written BEFORE `as`, so consume it first.
    const comment = this.parseTrailingComment();
    const asRef = this.parseOptionalAsName();

    // The bind / comment / `as` belong to the final hop only.
    const lastIdx = steps.length - 1;
    if (bind !== undefined || comment || asRef) {
      const last = steps[lastIdx]!;
      // Extend the step's source span to cover the trailing comment / `as
      // <label>` — both are consumed AFTER the hop loop finalized `source.end`,
      // so without this the backward-flow patcher's span-replace leaves the old
      // text behind (`… "c" "c"` / `… as cp as cp`). `as` is written last, so
      // its end wins when present; otherwise the comment's. `bind` is already
      // inside `source.start`.
      const trailingEnd = asRef?.source.end ?? comment?.source.end;
      steps[lastIdx] = {
        ...last,
        ...(bind !== undefined ? { bind, bindSource } : {}),
        ...(comment ? { comment: comment.text, commentSource: comment.source } : {}),
        ...(asRef ? { asName: asRef.name, asNameSource: asRef.source } : {}),
        ...(trailingEnd && last.source ? { source: { ...last.source, end: trailingEnd } } : {}),
      };
    }
    return steps;
  }

  /**
   * Optional trailing freehand comment string on an action step — the call
   * `> Callee "note"`, `go name "note"`, `do X "note"` forms (§5.5, PROCESS.md
   * §1). Consumed ONLY when the string is on the SAME line as the action, so a
   * following-line `Owner "str"` note or bare description stays a distinct step.
   * A string immediately followed by `:` is a CASE LABEL of an enclosing
   * `select` / `catch` (`select { "a": > A.x  "b": > B.y }`), NOT a comment —
   * consuming it would swallow the next case; so a trailing `:` disqualifies it.
   * Rendered on the step's edge + sidebar; never resolved or validated.
   */
  private parseTrailingComment(): { text: string; source: SourceSpan } | undefined {
    if (!this.check("STRING") || this.lineBreakBetween(this.previous(), this.peek())) return undefined;
    if (this.peekAhead(1)?.type === "COLON") return undefined; // it's a `"label":` case, not a comment
    const tok = this.advance();
    return { text: tok.value, source: tok.span };
  }

  /** Optional `as name` step alias (§7). Returns the name + its span. */
  private parseOptionalAsName(): { name: string; source: SourceSpan } | undefined {
    if (!this.matchToken("AS")) return undefined;
    const tok = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: "step name after 'as'" });
    return { name: tok.value, source: tok.span };
  }

  /**
   * A callee path `ident('.'ident)*` with an optional trailing dot. The
   * trailing dot (`Payments.`) marks the interface unspecified — a synthesized
   * anonymous handler stands in (§4.13.2).
   */
  private parseCallTarget(): { to: QualifiedName; toUnspecified: boolean; toSource: SourceSpan } {
    const start = this.peek().span;
    // v0.8.2: the first callee segment may be a reserved word (name position —
    // `Foo > out.x`, `> select.charge`), same as later `.member` segments.
    const firstTok = this.checkMemberSegment()
      ? this.advance()
      : this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: 'callee' });
    const segs: Name[] = [firstTok.value];
    let toUnspecified = false;
    while (this.check("DOT")) {
      this.advance(); // consume '.'
      // A following identifier extends the path only when it is on the same
      // line. A dot at end-of-line is a trailing dot (unspecified terminal,
      // §4.13.2) — it must NOT swallow the next statement's leading identifier.
      if (this.checkMemberSegment() && !this.lineBreakBetween(this.previous(), this.peek())) {
        segs.push(this.advance().value); // identifier or keyword-as-member (`Worker.process`)
      } else {
        toUnspecified = true; // trailing dot (EOL, or no following segment)
        break;
      }
    }
    const toSource: SourceSpan = { start: start.start, end: this.previous().span.end, file: start.file };
    return { to: segs, toUnspecified, toSource };
  }

  /**
   * Lookahead: does a bare process step start here? (PROCESS.md §1 — a bare step
   * needs no `process` wrapper.) True for:
   *   - a leading no-owner control / structural / terminator keyword
   *     (`each`/`if`/`select`/`try`/`parallel`/`reversible`/`await`/`do`/`go`/
   *     `fail`/`finish`/`unwind`), the `|` fan / `\` snake glyph, the `dist`
   *     owner prefix, or an anonymous head `>`;
   *   - an `IDENT =` bind, or a qualified name followed by `>` (call),
   *     a control keyword (owner-prefixed control), a string (owner note), or
   *     `do` (`Caller do X`).
   * Deliberately NOT `IDENT {` — at declaration scope that is a module body, not
   * an owner block (owner blocks live inside `process { }` / under a control).
   * Distinguishes a step from a field (`x: …`), a kind/sub-decl, or a module.
   */
  private isBareStepAhead(): boolean {
    const t = this.peek().type;
    if (
      t === "GT" || t === "PIPE" || t === "BACKSLASH" || t === "DIST" ||
      t === "EACH" || t === "IF" || t === "SELECT" || t === "TRY" ||
      t === "PARALLEL" || t === "REVERSIBLE" || t === "AWAIT" ||
      t === "DO" || t === "GO" || t === "FAIL" || t === "FINISH" || t === "UNWIND"
    ) {
      return true;
    }
    if (t !== "IDENTIFIER") return false;
    if (this.peekAhead(1)?.type === "EQUALS") return true;
    const after = this.tokenAfterQName()?.type;
    if (this.isControlKeyword(after)) {
      // Ambiguous `IDENT <control-kw>`: `Orders select { … }` (owner + construct)
      // vs `rest_read select` (interface of kind rest_read NAMED `select`, the
      // v0.8.2 keyword-as-name). A recognizable interface/surface type in the
      // lead makes it a declaration, not a step.
      const lead = this.peek().value;
      if (
        isLikelyInterfaceType(lead) ||
        isLikelySurfaceType(lead) ||
        lead === "interface" ||
        lead === "surface"
      ) {
        return false;
      }
      // Peek inside a directly-following `{` to distinguish an owner-prefixed
      // construct from a module named by the keyword:
      //   `Orders select { "a": > A.x }` → STRING+COLON inside → step
      //   `Orders reversible { > A.x }` → GT inside → step
      //   `Orders parallel { branch: … }` → BRANCH inside → step
      //   `microservice select {}` → empty body → module declaration
      //   `microservice select { field: value }` → IDENT+COLON inside → module
      // Keywords that take tokens between themselves and `{` (each, parallel,
      // select-with-head) produce a non-LBRACE afterBrace and fall through.
      let afterIdx = this.pos + 1;
      while (
        this.tokens[afterIdx]?.type === "DOT" &&
        this.tokens[afterIdx + 1]?.type === "IDENTIFIER"
      ) {
        afterIdx += 2;
      }
      // afterIdx now points at the control-keyword token.
      const afterBrace = this.tokens[afterIdx + 1];
      if (afterBrace?.type === "LBRACE") {
        const insideBrace = this.tokens[afterIdx + 2];
        const afterInsideBrace = this.tokens[afterIdx + 3];
        const isStepBody =
          insideBrace?.type === "BRANCH" || // parallel body
          insideBrace?.type === "GT" ||     // reversible/try/each body (arrow steps)
          (insideBrace?.type === "STRING" && afterInsideBrace?.type === "COLON"); // select case label
        if (!isStepBody) return false;
      }
      return true;
    }
    return after === "GT" || after === "STRING" || after === "DO";
  }

  /**
   * Wrap a run of consecutive bare-step statements in a name-less `ProcessDecl`
   * (§4.13). Consecutive bare steps are SIBLINGS of one anonymous process — they
   * share order and `|` ditto resolves against the previous sibling — so they are
   * grouped, not split one-per-statement. Each statement is parsed by the same
   * dispatcher used inside `process { }`, so owner-prefixed control / notes /
   * blocks / `do` all work bare. Anonymity is incompleteness — the resolver fills
   * implicit callers and validation surfaces it as a TODO.
   */
  private parseAnonymousProcess(): ProcessDecl | null {
    const startSpan = this.peek().span;
    const steps: ProcessStep[] = [];
    do {
      steps.push(...this.parseProcessSteps());
      // `;` is an inline step separator (PROCESS.md §1) — drain ANY run of them
      // so `A > B.x ;; A > B.y` groups into ONE anon process, exactly like the
      // newline-separated form (and like `;` inside a `process { }` block, whose
      // loop also tolerates repeated separators). A single match left a stray
      // `;` to start a fresh process, stranding a following `|` ditto.
      while (this.matchToken("SEMICOLON")) { /* drain */ }
      // v0.9.0 §2.6: a bare string BETWEEN two steps of the run is the run's
      // flow prose — it neither delimits the run nor joins the body/module
      // description. A trailing string (no step after) is NOT consumed: it
      // stays a description member, as before.
      while (this.check("STRING") && this.bareStepAfterString()) {
        const tok = this.advance();
        steps.push({ kind: "prose", text: tok.value, span: tok.span });
        while (this.matchToken("SEMICOLON")) { /* drain */ }
      }
    } while (!this.isAtEnd() && this.isBareStepAhead());
    const end = this.previous().span;
    // An anonymous process supports CALLS ONLY (§4.13). Branching, control
    // flow, notes/prose, terminators, `do` splices, and owner blocks must live
    // in a named `process Name { }`. Steps are still fully parsed above (clean
    // token consumption / recovery); here the non-call ones are flagged and
    // dropped. If nothing but a call survives no process is emitted — so a
    // stray `word "string"` never manufactures a phantom node.
    const calls = steps.filter((s) => {
      if (s.kind === "simple") return true;
      const sp = (s as { span?: SourceSpan }).span ?? (s as { source?: SourceSpan }).source;
      this.errorAt(sp ?? startSpan, "parse.anonProcessCallsOnly");
      return false;
    });
    if (calls.length === 0) return null;
    return {
      name: "",
      anonymous: true,
      steps: calls,
      source: { start: startSpan.start, end: end.end, file: startSpan.file },
    };
  }

  /**
   * `each [head] [in source] (body | retry-tail)` where head is an identifier,
   * string, or number, and source is a qualified name or string. The retry form
   * `each <bound> try { } catch { } else { }` (no `:` body, a `try` directly)
   * sets {@link RetryTail}; otherwise a plain loop body. `each {}` and
   * `each: A > B.x` are both valid plain forms.
   */
  private parseEachStep(owner?: StepOwner): EachStep {
    const start = (owner?.source ?? this.consume("EACH", "parse.expectedThingGot", { thing: "'each'" }).span);
    if (owner) this.consume("EACH", "parse.expectedThingGot", { thing: "'each'" });
    let label: string | undefined;
    let source: string | undefined;

    if (this.check("IDENTIFIER") || this.check("STRING") || this.check("NUMBER")) {
      label = this.advance().value;
    }

    if (this.matchToken("IN")) {
      if (this.check("STRING")) {
        source = this.advance().value;
      } else {
        source = this.parseQualifiedName().join(".");
      }
    }

    // Retry form: a `try` directly after the head (no `:` / `{` body) — the loop
    // attempts, restores on failure, retries, and runs `else` when exhausted.
    if (this.check("TRY")) {
      const retry = this.parseRetryTail();
      const end = this.previous().span;
      return {
        kind: "each",
        owner,
        label,
        source,
        steps: [],
        retry,
        span: { start: start.start, end: end.end, file: start.file },
      };
    }

    const steps = this.parseStepBodyOrOneLiner("each");
    const end = this.previous().span;
    return {
      kind: "each",
      owner,
      label,
      source,
      steps,
      span: { start: start.start, end: end.end, file: start.file },
    };
  }

  /** `try { } catch <label> { } else { }` retry tail of an `each` loop. */
  private parseRetryTail(): RetryTail {
    const start = this.consume("TRY", "parse.expectedThingGot", { thing: "'try'" }).span;
    const attempt = this.parseStepBodyOrOneLiner("each try");
    const catches: CatchClause[] = [];
    while (this.check("CATCH")) {
      const catchStart = this.advance().span;
      let label: string | undefined;
      if (this.check("STRING") || this.checkMemberSegment()) label = this.advance().value;
      const steps = this.parseStepBodyOrOneLiner("catch");
      catches.push({
        label,
        steps,
        span: { start: catchStart.start, end: this.previous().span.end, file: catchStart.file },
      });
    }
    let elseSteps: ProcessStep[] | undefined;
    if (this.check("ELSE")) {
      this.advance();
      elseSteps = this.parseStepBodyOrOneLiner("else");
    }
    const end = this.previous().span;
    return {
      attempt,
      catches,
      elseSteps,
      span: { start: start.start, end: end.end, file: start.file },
    };
  }

  /**
   * `if [cond] [body] (else if [cond] [body])* (else [body])?`.
   *
   * v0.8: condition and body are each OPTIONAL — a condition with no body is a
   * draft branch. Cond is an identifier or quoted string; body is a brace block
   * or a `: oneliner`.
   */
  private parseIfStep(owner?: StepOwner): IfStep {
    const start = (owner?.source ?? this.consume("IF", "parse.expectedThingGot", { thing: "'if'" }).span);
    if (owner) this.consume("IF", "parse.expectedThingGot", { thing: "'if'" });

    const firstCond = this.parseOptionalCondition();
    const firstSteps = this.parseOptionalStepBody();
    const branches: IfBranch[] = [
      {
        condition: firstCond?.text,
        ...(firstCond ? { conditionSource: firstCond.source } : {}),
        steps: firstSteps,
        span: { start: start.start, end: this.previous().span.end, file: start.file },
      },
    ];

    let elseBranch: IfBranch | undefined;
    let elseSteps: readonly ProcessStep[] | undefined;

    while (this.check("ELSE")) {
      const elseTok = this.advance();
      if (this.matchToken("IF")) {
        const cond = this.parseOptionalCondition();
        const steps = this.parseOptionalStepBody();
        branches.push({
          condition: cond?.text,
          ...(cond ? { conditionSource: cond.source } : {}),
          steps,
          span: { start: elseTok.span.start, end: this.previous().span.end, file: elseTok.span.file },
        });
      } else {
        elseSteps = this.parseOptionalStepBody();
        elseBranch = {
          steps: elseSteps,
          span: { start: elseTok.span.start, end: this.previous().span.end, file: elseTok.span.file },
        };
        break;
      }
    }

    const end = this.previous().span;
    return {
      kind: "if",
      owner,
      branches,
      elseBranch,
      elseSteps,
      span: { start: start.start, end: end.end, file: start.file },
    };
  }

  /**
   * `select [count] [head] { case-label body … }` — the inclusive multi-way
   * choice (v0.8, replaces `switch`). `select one` / `select N` set the count.
   * Head and case aspects are identifiers or quoted strings.
   */
  private parseSelectStep(owner?: StepOwner): SelectStep {
    const start = (owner?.source ?? this.consume("SELECT", "parse.expectedThingGot", { thing: "'select'" }).span);
    if (owner) this.consume("SELECT", "parse.expectedThingGot", { thing: "'select'" });

    let count: SelectCount | undefined;
    if (this.check("ONE")) {
      this.advance();
      count = { kind: "one" };
    } else if (this.check("NUMBER")) {
      const tok = this.advance();
      count = { kind: "n", n: Number(tok.value), source: tok.span };
    }

    let head: string | undefined;
    let headSource: SourceSpan | undefined;
    if (this.check("STRING") || this.checkMemberSegment()) {
      const tok = this.advance();
      head = tok.value;
      headSource = tok.span;
    }

    this.consume("LBRACE", "parse.expectedOpenAfterGot", { after: "'select' header" });

    const cases: SelectCase[] = [];
    while (!this.isAtEnd() && !this.check("RBRACE")) {
      if (this.matchToken("SEMICOLON")) continue;
      if (this.check("RBRACE")) break;
      const caseStart = this.peek().span;
      // v0.9.0 §2.6: a STRING is a case label only when `:` or `{` follows;
      // otherwise it is flow prose, attached to the previous case's steps.
      if (
        this.check("STRING") &&
        this.peekAhead(1)?.type !== "COLON" &&
        this.peekAhead(1)?.type !== "LBRACE"
      ) {
        const tok = this.advance();
        const prevCase = cases[cases.length - 1];
        if (prevCase) {
          (prevCase.steps as ProcessStep[]).push({ kind: "prose", text: tok.value, span: tok.span });
        } else {
          this.error(tok, "parse.caseLabelBeforeProse");
        }
        continue;
      }
      let label: string;
      let labelSource: SourceSpan;
      // A case label is a routing tag — a reserved keyword (`one`, `each`, …) is
      // unambiguous here (the count is in the header), so accept it.
      if (this.check("STRING") || this.checkMemberSegment()) {
        const tok = this.advance();
        label = tok.value;
        labelSource = tok.span;
      } else {
        this.error(this.peek(), "parse.expectedCaseLabel");
        this.advance();
        continue;
      }
      const steps = this.parseStepBodyOrOneLiner("select case");
      cases.push({
        label,
        labelSource,
        steps,
        span: { start: caseStart.start, end: this.previous().span.end, file: caseStart.file },
      });
    }

    this.consume("RBRACE", "parse.expectedCloseAfterGot", { what: 'select body' });
    const end = this.previous().span;
    return {
      kind: "select",
      owner,
      count,
      head,
      ...(headSource ? { headSource } : {}),
      cases,
      span: { start: start.start, end: end.end, file: start.file },
    };
  }

  /**
   * `try Body (catch [Label] Body)*` — the plain guard. Multiple catches
   * allowed; each catch label optional and freeform.
   */
  private parseTryStep(owner?: StepOwner): TryStep {
    const start = (owner?.source ?? this.consume("TRY", "parse.expectedThingGot", { thing: "'try'" }).span);
    if (owner) this.consume("TRY", "parse.expectedThingGot", { thing: "'try'" });
    const trySteps = this.parseStepBodyOrOneLiner("try");

    const catches: CatchClause[] = [];
    while (this.check("CATCH")) {
      const catchStart = this.advance().span;
      let label: string | undefined;
      if (this.check("STRING") || this.checkMemberSegment()) {
        label = this.advance().value;
      }
      const steps = this.parseStepBodyOrOneLiner("catch");
      catches.push({
        label,
        steps,
        span: { start: catchStart.start, end: this.previous().span.end, file: catchStart.file },
      });
    }

    const end = this.previous().span;
    return {
      kind: "try",
      owner,
      trySteps,
      catches,
      span: { start: start.start, end: end.end, file: start.file },
    };
  }

  /**
   * `parallel [out] [join|race spec] <body>` (PROCESS.md §5). The prefix merge
   * clause sets re-convergence; the body is plain `{ branch … }`,
   * `select [count] { branch … }`, or an `each <head> { … }` fan-out.
   */
  private parseParallelStep(owner?: StepOwner): ParallelStep {
    const start = (owner?.source ?? this.consume("PARALLEL", "parse.expectedThingGot", { thing: "'parallel'" }).span);
    if (owner) this.consume("PARALLEL", "parse.expectedThingGot", { thing: "'parallel'" });

    const merge = this.parseMergeClause();

    // v0.9.0 §2.2: `await <head>` boundary clause — implies race semantics.
    let awaitHead: string | undefined;
    let awaitHeadSource: SourceSpan | undefined;
    let awaitAs: string | undefined;
    let awaitAsSource: SourceSpan | undefined;
    if (this.check("AWAIT")) {
      if (merge?.policy) {
        this.error(this.peek(), "parse.awaitNoJoinRace");
      }
      this.advance();
      const head = this.parseAwaitTarget();
      awaitHead = head.text;
      awaitHeadSource = head.source;
      // Optional `as <label>` alias on the boundary (`parallel await 1 day as grace`).
      if (this.matchToken("AS")) {
        const label = this.consume("IDENTIFIER", "Expected an alias name after 'as'");
        awaitAs = label.value;
        awaitAsSource = label.span;
      }
      if (this.check("EACH") || this.check("SELECT")) {
        this.error(this.peek(), "parse.eachSelectNoAwait");
      }
    }

    // Fan-out form: `parallel [merge] each <head> { … }`.
    if (this.check("EACH")) {
      this.advance();
      let label: string | undefined;
      let source: string | undefined;
      if (this.check("IDENTIFIER") || this.check("STRING") || this.check("NUMBER")) {
        label = this.advance().value;
      }
      if (this.matchToken("IN")) {
        source = this.check("STRING") ? this.advance().value : this.parseQualifiedName().join(".");
      }
      const steps = this.parseStepBodyOrOneLiner("parallel each");
      const fanOut: ParallelFanOut = {
        label,
        source,
        steps,
        span: { start: start.start, end: this.previous().span.end, file: start.file },
      };
      const end = this.previous().span;
      return {
        kind: "parallel",
        owner,
        merge,
        branches: [],
        fanOut,
        span: { start: start.start, end: end.end, file: start.file },
      };
    }

    // Concurrent-inclusive form: `parallel [merge] select [count] { branch … }`.
    let select = false;
    let selectCount: SelectCount | undefined;
    if (this.check("SELECT")) {
      this.advance();
      select = true;
      if (this.check("ONE")) {
        this.advance();
        selectCount = { kind: "one" };
      } else if (this.check("NUMBER")) {
        const tok = this.advance();
        selectCount = { kind: "n", n: Number(tok.value), source: tok.span };
      }
    }

    this.consume("LBRACE", "parse.expectedOpenAfterGot", { after: "'parallel'" });
    let branches: ParallelBranch[];
    if (this.check("BRANCH") || select || this.check("RBRACE")) {
      if (awaitHead !== undefined && this.check("BRANCH")) {
        this.error(this.peek(), "parse.awaitSingleBranch");
      }
      branches = this.parseParallelBranches();
    } else {
      // v0.9.0 §2.3: a lone body without the `branch` keyword is ONE implicit
      // branch (`parallel out { … }`, `parallel await 1 day { … }`).
      const bStart = this.peek().span;
      const steps: ProcessStep[] = [];
      while (!this.isAtEnd() && !this.check("RBRACE")) {
        if (this.matchToken("SEMICOLON")) continue;
        const before = this.pos;
        const parsed = this.parseProcessSteps();
        if (parsed.length) steps.push(...parsed);
        else if (this.pos === before) this.advance();
        this.matchToken("SEMICOLON");
      }
      branches = [
        {
          steps,
          implicit: true,
          span: { start: bStart.start, end: this.previous().span.end, file: bStart.file },
        },
      ];
    }
    this.consume("RBRACE", "parse.expectedCloseAfterGot", { what: 'parallel body' });
    // v0.9.0 §2.2: optional `else { … }` timeout branch — await-clause only,
    // binds to the NEAREST await-clause.
    let elseSteps: ProcessStep[] | undefined;
    if (awaitHead !== undefined && this.check("ELSE")) {
      this.advance();
      elseSteps = this.parseBlockSteps("else");
    }
    const end = this.previous().span;
    return {
      kind: "parallel",
      owner,
      merge,
      ...(awaitHead !== undefined
        ? { awaitHead, awaitHeadSource, ...(awaitAs !== undefined ? { awaitAs, awaitAsSource } : {}), ...(elseSteps ? { elseSteps } : {}) }
        : {}),
      ...(select ? { select: true } : {}),
      selectCount,
      branches,
      span: { start: start.start, end: end.end, file: start.file },
    };
  }

  /** The `[out] [join|race spec]` prefix merge clause; undefined when absent. */
  private parseMergeClause(): MergeClause | undefined {
    const start = this.peek().span;
    let out = false;
    let policy: "join" | "race" | undefined;
    let count: number | undefined;
    let countSource: SourceSpan | undefined;
    let bool: MergeBool | undefined;

    if (this.matchToken("OUT")) out = true;
    if (this.check("JOIN") || this.check("RACE")) {
      policy = this.advance().type === "JOIN" ? "join" : "race";
      if (this.check("NUMBER")) {
        const tok = this.advance();
        count = Number(tok.value);
        countSource = tok.span;
      } else if (
        (this.checkMemberSegment() && !this.isStepStartKeyword(this.peek().type)) ||
        this.check("LPAREN")
      ) {
        // A bare branch-name threshold may be a reserved keyword (`join override`)
        // — `parseMergeBoolAtom` accepts it; the entry guard must too. But NOT a
        // step-starting keyword: `each`/`select` after the merge are the fan-out /
        // concurrent-inclusive forms, not a threshold name.
        bool = this.parseMergeBoolOr();
      }
    }

    if (!out && policy === undefined) return undefined;
    return {
      ...(out ? { out: true } : {}),
      policy,
      count,
      countSource,
      bool,
      source: { start: start.start, end: this.previous().span.end, file: start.file },
    };
  }

  /** `bool_and ("or" bool_and)*` — boolean over branch names (`or`/`and` are
   *  case-insensitive identifiers, not keywords). */
  private parseMergeBoolOr(): MergeBool {
    const terms = [this.parseMergeBoolAnd()];
    while (this.check("IDENTIFIER") && this.peek().value.toLowerCase() === "or") {
      this.advance();
      terms.push(this.parseMergeBoolAnd());
    }
    return terms.length === 1 ? terms[0]! : { kind: "or", terms };
  }

  private parseMergeBoolAnd(): MergeBool {
    const terms = [this.parseMergeBoolAtom()];
    while (this.check("IDENTIFIER") && this.peek().value.toLowerCase() === "and") {
      this.advance();
      terms.push(this.parseMergeBoolAtom());
    }
    return terms.length === 1 ? terms[0]! : { kind: "and", terms };
  }

  private parseMergeBoolAtom(): MergeBool {
    if (this.matchToken("LPAREN")) {
      const inner = this.parseMergeBoolOr();
      this.consume("RPAREN", "parse.expectedThingGot", { thing: "')' in merge condition" });
      return inner;
    }
    // A branch name in a merge condition is a routing tag — accept a reserved
    // keyword (`override`, `out`, …) as well as a plain identifier.
    const tok = this.checkMemberSegment()
      ? this.advance()
      : this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: 'branch name in merge condition' });
    return { kind: "name", name: tok.value, source: tok.span };
  }

  /** Parse the `branch …` members of a `parallel { … }` body (cursor inside). */
  private parseParallelBranches(): ParallelBranch[] {
    const branches: ParallelBranch[] = [];
    while (!this.isAtEnd() && !this.check("RBRACE")) {
      if (this.matchToken("SEMICOLON")) continue;
      if (this.check("RBRACE")) break;
      if (this.check("BRANCH")) {
        branches.push(this.parseBranch());
        this.matchToken("SEMICOLON");
        continue;
      }
      this.error(this.peek(), "parse.parallelBranchStart", { open: OPEN_BRACE });
      this.advance();
    }
    return branches;
  }

  /** `branch [name] (: step | { block } | control)`. */
  private parseBranch(): ParallelBranch {
    const start = this.consume("BRANCH", "parse.expectedThingGot", { thing: "'branch'" }).span;
    let name: string | undefined;
    let nameSource: SourceSpan | undefined;
    // A branch name is an identifier, or a reserved keyword that does NOT start a
    // construct (`branch override:`, `branch out:`) — `branch if …` is a nested
    // if, so step-starting keywords are excluded.
    if (
      this.check("IDENTIFIER") ||
      (RESERVED_KEYWORDS.has(this.peek().value) && !this.isStepStartKeyword(this.peek().type))
    ) {
      const tok = this.advance();
      name = tok.value;
      nameSource = tok.span;
    }
    let steps: ProcessStep[];
    if (this.matchToken("COLON")) {
      steps = this.parseColonSteps();
    } else if (this.check("LBRACE")) {
      steps = this.parseBlockSteps("branch");
    } else {
      // A nested control construct without a `:` (`branch if …`, `branch parallel …`).
      steps = this.parseProcessSteps();
    }
    const end = this.previous().span;
    return {
      name,
      nameSource,
      steps,
      span: { start: start.start, end: end.end, file: start.file },
    };
  }

  /** `reversible [on …] { item* }` — saga span (PROCESS.md §8). */
  private parseReversibleStep(owner?: StepOwner): ReversibleStep {
    const start = (owner?.source ?? this.consume("REVERSIBLE", "parse.expectedThingGot", { thing: "'reversible'" }).span);
    if (owner) this.consume("REVERSIBLE", "parse.expectedThingGot", { thing: "'reversible'" });

    let trigger: string | undefined;
    let triggerSource: SourceSpan | undefined;
    if (this.matchToken("ON")) {
      if (this.check("STRING") || this.checkMemberSegment()) {
        const tok = this.advance();
        trigger = tok.value;
        triggerSource = tok.span;
      } else {
        this.error(this.peek(), "parse.expectedTriggerAfterOn");
      }
    }

    this.consume("LBRACE", "parse.expectedOpenAfterGot", { after: "'reversible' header" });
    const items: ReversibleItem[] = [];
    while (!this.isAtEnd() && !this.check("RBRACE")) {
      if (this.matchToken("SEMICOLON")) continue;
      if (this.check("RBRACE")) break;
      const before = this.pos;
      const item = this.parseReversibleItem();
      if (item) items.push(item);
      else if (this.pos === before) this.advance();
      this.matchToken("SEMICOLON");
    }
    this.consume("RBRACE", "parse.expectedCloseAfterGot", { what: 'reversible body' });
    const end = this.previous().span;
    return {
      kind: "reversible",
      owner,
      trigger,
      triggerSource,
      items,
      span: { start: start.start, end: end.end, file: start.file },
    };
  }

  /** If a forward step / unwind action ate a trailing `as <name>` that belongs to
   *  the reversible ITEM, pull it off and return the name + the stripped node.
   *  Only a simple step carries `asName`; anything else returns undefined. */
  private liftItemAs(
    node: ProcessStep | BlockStep | undefined
  ): { name: string; source?: SourceSpan; node: ProcessStep | BlockStep } | undefined {
    if (node && node.kind === "simple" && node.asName) {
      return {
        name: node.asName,
        source: node.asNameSource,
        node: { ...node, asName: undefined, asNameSource: undefined },
      };
    }
    return undefined;
  }

  /** One `reversible` item: forward step/block + optional `unwind` + `as`, or a
   *  reversal-only `unwind` line, or a bare description string (skipped). */
  private parseReversibleItem(): ReversibleItem | undefined {
    const start = this.peek().span;

    // v0.9.0 §2.6: a bare string inside a reversible block is flow prose —
    // an item with a prose forward and no unwind (skipped on reversal).
    if (this.check("STRING")) {
      const tok = this.advance();
      return {
        forward: { kind: "prose", text: tok.value, span: tok.span },
        span: tok.span,
      };
    }

    // Reversal-only line: `unwind X [as name]` with no forward action.
    if (this.check("UNWIND")) {
      let unwind = this.parseUnwindClause();
      const asRef = this.parseOptionalAsName();
      let asName = asRef?.name;
      let asNameSource: SourceSpan | undefined = asRef?.source;
      if (!asName && unwind) {
        const lifted = this.liftItemAs(unwind.action);
        if (lifted) {
          asName = lifted.name;
          asNameSource = lifted.source;
          unwind = { ...unwind, action: lifted.node as typeof unwind.action };
        }
      }
      const end = this.previous().span;
      return {
        unwind,
        asName,
        asNameSource,
        span: { start: start.start, end: end.end, file: start.file },
      };
    }

    // Forward action: a bare block, or a process step (possibly a chain).
    let forward: ProcessStep | BlockStep | undefined;
    if (this.check("LBRACE")) {
      forward = this.parseBlockNode();
    } else {
      const parsed = this.parseProcessSteps();
      if (parsed.length === 1) {
        forward = parsed[0]!;
      } else if (parsed.length > 1) {
        // A chain → wrap its hops in a transparent block so the item is one node.
        forward = {
          kind: "block",
          chain: true,
          steps: parsed,
          span: { start: start.start, end: this.previous().span.end, file: start.file },
        };
      }
    }
    if (!forward) return undefined;

    // An `unwind` attaches to this forward only when it sits on the SAME line;
    // an `unwind` after a line break is a separate reversal-only item.
    let unwind: UnwindClause | undefined;
    if (this.check("UNWIND") && !this.lineBreakBetween(this.previous(), this.peek())) {
      const unwindTok = this.peek();
      unwind = this.parseUnwindClause();
      // `unwind` may only attach to a simple step or a block (PROCESS.md §8 — no
      // nesting in control). A control forward (`if`/`select`/`each`/`try`/
      // `parallel`/…) with an `unwind` is ambiguous about which step it undoes.
      if (forward.kind !== "simple" && forward.kind !== "block") {
        this.error(unwindTok, "parse.unwindAttachSimple");
      }
    }
    const asRef = this.parseOptionalAsName();
    let asName = asRef?.name;
    let asNameSource: SourceSpan | undefined = asRef?.source;
    // `as` after a forward/unwind names the reversible ITEM (the `go` target,
    // PROCESS.md §7), but the inner `parseStepChain` greedily consumed it onto
    // the call/action — lift it back up to the item.
    if (!asName) {
      const lifted = this.liftItemAs(unwind?.action ?? forward);
      if (lifted) {
        asName = lifted.name;
        asNameSource = lifted.source;
        if (unwind) unwind = { ...unwind, action: lifted.node as typeof unwind.action };
        else forward = lifted.node;
      }
    }
    const end = this.previous().span;
    return {
      forward,
      unwind,
      asName,
      asNameSource,
      span: { start: start.start, end: end.end, file: start.file },
    };
  }

  /**
   * `unwind <action>` — the reverse action paired with a forward step/block. The
   * action must be a complete statement: a call (`>`), `fail`/`finish`, `do`, or
   * a block. A bare interface with no `>` (`unwind refund`) is an error. Returns
   * undefined on the error path (after consuming for recovery).
   */
  private parseUnwindClause(): UnwindClause | undefined {
    const start = this.consume("UNWIND", "parse.expectedThingGot", { thing: "'unwind'" }).span;
    let action: SimpleStep | FailStep | FinishStep | DoStep | BlockStep | undefined;

    if (this.check("LBRACE")) {
      action = this.parseBlockNode();
    } else if (this.check("FAIL")) {
      action = this.parseFailStep();
    } else if (this.check("FINISH")) {
      action = this.parseFinishStep();
    } else if (this.check("DO")) {
      action = this.parseDoStep();
    } else if (this.check("GT")) {
      const steps = this.parseStepChain();
      action = this.collapseChainAction(steps, start);
    } else if (this.check("PIPE")) {
      // `unwind | > X` / `unwind | do X` — a ditto-owner unwind action (caller =
      // previous sibling), symmetric with the forward `| > X` step. Keeps
      // text ⇄ IR round-trip closed: the printer emits `unwind |` for a
      // ditto-owner unwind action, which must reparse.
      const pipe = this.advance();
      const owner: StepOwner = { form: "ditto", source: pipe.span };
      if (this.check("DO")) {
        action = this.parseDoStep(owner);
      } else {
        const steps = this.parseStepChain(owner);
        action = this.collapseChainAction(steps, start);
      }
    } else if (this.check("IDENTIFIER")) {
      // A call requires a `>`; a bare interface (`unwind Payments.refund`) has no
      // caller and is an error.
      const term = this.tokenAfterQName();
      if (term?.type === "GT") {
        const steps = this.parseStepChain();
        action = this.collapseChainAction(steps, start);
      } else if (term?.type === "DO") {
        // `unwind Caller do X` — a template-caller-prefixed subprocess splice
        // (PROCESS.md §7 `Caller do X` + §8 `do` as an unwind action combine).
        const caller = this.parseExplicitOwner();
        action = this.parseDoStep(caller);
      } else {
        this.error(this.peek(), "parse.unwindActionKinds");
        this.parseQualifiedName(); // consume to recover
        return undefined;
      }
    } else if (this.check("BACKSLASH")) {
      // §4.13.4 rule 5: an `unwind` is not a sibling of the run around it, so
      // there is no tail to continue — `\` here is always an error.
      this.error(this.peek(), "parse.snakeRequiresCall");
      return undefined;
    } else {
      this.error(this.peek(), "parse.expectedActionAfterUnwind");
      return undefined;
    }

    if (!action) return undefined;
    const end = this.previous().span;
    return { action, source: { start: start.start, end: end.end, file: start.file } };
  }

  /** Collapse a parsed call chain into a single unwind action (block if >1 hop). */
  private collapseChainAction(
    steps: readonly SimpleStep[],
    start: SourceSpan
  ): SimpleStep | BlockStep | undefined {
    if (steps.length === 0) return undefined;
    if (steps.length === 1) return steps[0]!;
    return {
      kind: "block",
      chain: true,
      steps,
      span: { start: start.start, end: this.previous().span.end, file: start.file },
    };
  }

  /** A bare `{ … }` grouping (reversible item / unwind action), cursor at `{`. */
  private parseBlockNode(): BlockStep {
    const start = this.peek().span;
    const steps = this.parseBlockSteps("block");
    const end = this.previous().span;
    return {
      kind: "block",
      steps,
      span: { start: start.start, end: end.end, file: start.file },
    };
  }

  /** `await <free-form>` — a timed / event wait owned by the waiter. */
  private parseAwaitStep(owner?: StepOwner): AwaitStep {
    const start = (owner?.source ?? this.consume("AWAIT", "parse.expectedThingGot", { thing: "'await'" }).span);
    if (owner) this.consume("AWAIT", "parse.expectedThingGot", { thing: "'await'" });

    const head = this.parseAwaitTarget();
    const target = head.text;
    const targetSource = head.source;
    const end = this.previous().span;
    return {
      kind: "await",
      owner,
      target,
      targetSource,
      span: { start: start.start, end: end.end, file: start.file },
    };
  }

  /** The free-form await head: a string, or identifier/number words on one
   *  line (`1 day`, `until ready`, `"0 3 1 * *"`). Shared by the await step
   *  and the `parallel await` clause (v0.9.0 §2.2). Empty head = error. */
  private parseAwaitTarget(): { text: string; source?: SourceSpan } {
    if (this.check("STRING")) {
      const tok = this.advance();
      return { text: tok.value, source: tok.span };
    }
    // Collect identifier / number tokens on the same line (`1 day`, `until ready`).
    const parts: string[] = [];
    const tStart = this.peek().span;
    while (
      (this.check("IDENTIFIER") || this.check("NUMBER")) &&
      (parts.length === 0 || !this.lineBreakBetween(this.previous(), this.peek()))
    ) {
      parts.push(this.advance().value);
    }
    if (parts.length === 0) {
      this.error(this.peek(), "parse.expectedAwaitTarget");
      return { text: "" };
    }
    return {
      text: parts.join(" "),
      source: { start: tStart.start, end: this.previous().span.end, file: tStart.file },
    };
  }

  /** `go <name>` — re-enter a step named with `as`. Ownerless. */
  private parseGoStep(): GoStep {
    const start = this.consume("GO", "parse.expectedThingGot", { thing: "'go'" }).span;
    const tok = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: "step name after 'go'" });
    const comment = this.parseTrailingComment();
    const end = this.previous().span;
    return {
      kind: "go",
      target: tok.value,
      ...(comment ? { comment: comment.text, commentSource: comment.source } : {}),
      targetSource: tok.span,
      span: { start: start.start, end: end.end, file: start.file },
    };
  }

  /** `Owner { … }` / `| { … }` / `dist { … }` — set the owner for a run of steps. */
  private parseOwnerBlock(owner: StepOwner): OwnerBlockStep {
    const start = owner.source ?? this.peek().span;
    const steps = this.parseBlockSteps("owner block");
    const end = this.previous().span;
    return {
      kind: "owner_block",
      owner,
      steps,
      span: { start: start.start, end: end.end, file: start.file },
    };
  }

  /**
   * `[Caller] do Target(args?)` — invoke a (potentially qualified) subprocess.
   * The optional caller prefix is the template default-caller (Spec §5.5).
   */
  private parseDoStep(templateCaller?: StepOwner): DoStep {
    const start = templateCaller?.source ?? this.peek().span;
    this.consume("DO", "parse.expectedThingGot", { thing: "'do'" });
    const targetStart = this.peek().span;
    const target = this.parseQualifiedName();
    const targetSource: SourceSpan = {
      start: targetStart.start,
      end: this.previous().span.end,
      file: targetStart.file,
    };
    const args = this.parseOptionalArgList();
    // `do X as pay1` (§7) — the splice's name, doubling as the test-namespace
    // for choices/steps inside it (design §6). Parsed before the trailing note.
    const asRef = this.parseOptionalAsName();
    const comment = this.parseTrailingComment();
    const end = this.previous().span;
    return {
      kind: "do",
      target,
      ...(templateCaller ? { templateCaller } : {}),
      args,
      ...(asRef ? { asName: asRef.name, asNameSource: asRef.source } : {}),
      ...(comment ? { comment: comment.text, commentSource: comment.source } : {}),
      span: { start: start.start, end: end.end, file: start.file },
      targetSource,
    };
  }

  /** `fail [String]`. Optional reason string. */
  private parseFailStep(): FailStep {
    const start = this.consume("FAIL", "parse.expectedThingGot", { thing: "'fail'" }).span;
    const all = this.matchTerminatorAll();
    let reason: string | undefined;
    let reasonSource: SourceSpan | undefined;
    // Same-line only — a NEXT-LINE bare string is flow prose (§2.6), not the
    // reason (same guard class as `all` and trailing call comments).
    if (this.check("STRING") && !this.lineBreakBetween(this.previous(), this.peek())) {
      const tok = this.advance();
      reason = tok.value;
      reasonSource = tok.span; // quotes included — the addressable edit span
    }
    const end = this.previous().span;
    return {
      kind: "fail",
      ...(all ? { all: true } : {}),
      reason,
      ...(reasonSource ? { reasonSource } : {}),
      span: { start: start.start, end: end.end, file: start.file },
    };
  }

  /** `finish [all] [String]`. Optional success label (same-line only — a
   *  next-line string is flow prose, §2.6). */
  private parseFinishStep(): FinishStep {
    const start = this.consume("FINISH", "parse.expectedThingGot", { thing: "'finish'" }).span;
    const all = this.matchTerminatorAll();
    let label: string | undefined;
    let labelSource: SourceSpan | undefined;
    if (this.check("STRING") && !this.lineBreakBetween(this.previous(), this.peek())) {
      const tok = this.advance();
      label = tok.value;
      labelSource = tok.span; // quotes included — the addressable edit span
    }
    const end = this.previous().span;
    return {
      kind: "finish",
      ...(all ? { all: true } : {}),
      label,
      ...(labelSource ? { labelSource } : {}),
      span: { start: start.start, end: end.end, file: start.file },
    };
  }

  /** v0.9.0 §2.4: the `all` modifier after `fail`/`finish` — contextual,
   *  consumed only on the SAME line, immediately after the keyword (a caller
   *  or note actor named `all` on the next line must never merge). */
  private matchTerminatorAll(): boolean {
    if (
      this.check("IDENTIFIER") &&
      this.peek().value === "all" &&
      !this.lineBreakBetween(this.previous(), this.peek())
    ) {
      this.advance();
      return true;
    }
    return false;
  }

  /**
   * Helper: consume either `{ Step* }` or `: OneLinerStep(; OneLinerStep)*`.
   * Used for every block-bearing construct so the `:` shortcut and the `;`
   * inline separator are available uniformly.
   */
  private parseStepBodyOrOneLiner(context: string): ProcessStep[] {
    if (this.matchToken("COLON")) {
      return this.parseColonSteps();
    }
    if (!this.check("LBRACE")) {
      this.error(this.peek(), "parse.expectedOpenOrColonAfter", { open: OPEN_BRACE, context });
      return [];
    }
    return this.parseBlockSteps(context);
  }

  /** A `:`-introduced one-liner body: `step (";" step)*`. */
  private parseColonSteps(): ProcessStep[] {
    const steps: ProcessStep[] = [];
    steps.push(...this.parseProcessSteps());
    while (this.check("SEMICOLON")) {
      this.advance();
      if (this.check("RBRACE") || this.isAtEnd()) break;
      const before = this.pos;
      const parsed = this.parseProcessSteps();
      if (parsed.length) steps.push(...parsed);
      else if (this.pos === before) break;
    }
    return steps;
  }

  /** A brace `{ step (newline|";" step)* }` block; consumes both braces. */
  private parseBlockSteps(context: string): ProcessStep[] {
    this.consume("LBRACE", "parse.expectedOpenToOpenGot", { open: OPEN_BRACE, context });
    const steps: ProcessStep[] = [];
    while (!this.isAtEnd() && !this.check("RBRACE")) {
      if (this.matchToken("SEMICOLON")) continue;
      if (this.check("RBRACE")) break;
      const before = this.pos;
      const parsed = this.parseProcessSteps();
      if (parsed.length) steps.push(...parsed);
      else if (this.pos === before) this.advance();
      this.matchToken("SEMICOLON");
    }
    this.consume("RBRACE", "parse.expectedCloseToCloseGot", { close: CLOSE_BRACE, context });
    return steps;
  }

  /** Optional `(name1, name2, ...)` argument list. Names are bare identifiers. */
  private parseOptionalArgList(): string[] | undefined {
    if (!this.matchToken("LPAREN")) return undefined;
    const args: string[] = [];
    if (!this.check("RPAREN")) {
      args.push(this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: 'argument name' }).value);
      while (this.matchToken("COMMA")) {
        args.push(this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: 'argument name' }).value);
      }
    }
    this.consume("RPAREN", "parse.expectedThingGot", { thing: "')' to close argument list" });
    return args;
  }

  /** Optional `(name1, name2)` parameter list — same shape as args. */
  private parseOptionalParamList(): string[] {
    const args = this.parseOptionalArgList();
    return args ?? [];
  }

  /**
   * Optional condition for an `if` / `else if` arm: an identifier or quoted
   * string. v0.8: the condition may be omitted (a body-only or draft branch),
   * so a non-condition token (`{`, `:`, `else`, a step head) yields undefined.
   */
  private parseOptionalCondition(): { text: string; source: SourceSpan } | undefined {
    if (this.check("STRING") || this.check("IDENTIFIER")) {
      const tok = this.advance();
      return { text: tok.value, source: tok.span };
    }
    return undefined;
  }

  /**
   * Optional step body: a brace block or a `:`-introduced one-liner. v0.8: a
   * branch body may be omitted entirely (a draft branch), in which case neither
   * `{` nor `:` follows and this returns undefined. (Distinct from the
   * module/surface `parseOptionalBody`, which returns a {@link Body}.)
   */
  private parseOptionalStepBody(): readonly ProcessStep[] | undefined {
    if (this.matchToken("COLON")) return this.parseColonSteps();
    if (this.check("LBRACE")) return this.parseBlockSteps("body");
    return undefined;
  }

  // -------------------------------------------------------------------------
  // View declaration
  // -------------------------------------------------------------------------

  /**
   * A v0.10 declaration head (`query` / `policy`) at token offset `off`: the
   * word followed by a name (STABLE_ID or IDENTIFIER), NOT by `:`/`.` (which
   * would make it a field — the member-head dispatch rule, §5.2).
   */
  private isV010DeclHeadAt(off: number, word: string): boolean {
    const head = off === 0 ? this.peek() : this.peekAhead(off);
    if (!(head?.type === "IDENTIFIER" && head.value === word)) return false;
    const next = this.peekAhead(off + 1)?.type;
    return next === "STABLE_ID" || next === "IDENTIFIER";
  }

  /** True when the token at offset `off` is an IDENTIFIER with value `word`. */
  private isIdentValue(off: number, word: string): boolean {
    const tok = off === 0 ? this.peek() : this.peekAhead(off);
    return tok?.type === "IDENTIFIER" && tok.value === word;
  }

  /** Delegate a `style bundle` declaration to the v0.10 sub-parser. */
  private parseV010StyleBundle(): StyleBundleDecl {
    const sub = new V010DeclParser(this.tokens, this.pos);
    sub.rawSource = this.source;
    const decl = sub.parseStyleBundle();
    this.pos = sub.pos;
    for (const e of sub.errors) this.errors.push({ ...e });
    return decl;
  }

  /** Delegate a `lens` declaration to the v0.10 sub-parser. */
  private parseV010Lens(): LensDecl {
    const sub = new V010DeclParser(this.tokens, this.pos);
    sub.rawSource = this.source;
    const decl = sub.parseLens();
    this.pos = sub.pos;
    for (const e of sub.errors) this.errors.push({ ...e });
    return decl;
  }

  /**
   * `use policy <Name> from …` — governance instantiation, distinct from an
   * import. `use policy from X` (name after `policy` is `from`) is still an
   * ordinary import of a thing named `policy`, so require a real name token.
   */
  private isUsePolicyAhead(): boolean {
    if (!this.check("USE")) return false;
    if (!(this.peekAhead(1)?.type === "IDENTIFIER" && this.peekAhead(1)?.value === "policy")) return false;
    return this.peekAhead(2)?.type === "IDENTIFIER";
  }

  /** Delegate a `use policy` statement to the v0.10 sub-parser. */
  private parseV010UsePolicy(): UsePolicyStmt {
    const sub = new V010DeclParser(this.tokens, this.pos);
    sub.rawSource = this.source;
    const decl = sub.parseUsePolicy();
    this.pos = sub.pos;
    for (const e of sub.errors) this.errors.push({ ...e });
    return decl;
  }

  /** Delegate a `query` declaration to the v0.10 sub-parser, syncing pos + errors. */
  private parseV010Query(exported: boolean): QueryDecl {
    const sub = new V010DeclParser(this.tokens, this.pos);
    sub.rawSource = this.source;
    const decl = sub.parseQuery(exported);
    this.pos = sub.pos;
    for (const e of sub.errors) this.errors.push({ ...e });
    return decl;
  }

  /** Delegate a `policy` declaration to the v0.10 sub-parser, syncing pos + errors. */
  private parseV010Policy(exported: boolean): PolicyDecl {
    const sub = new V010DeclParser(this.tokens, this.pos);
    sub.rawSource = this.source;
    const decl = sub.parsePolicy(exported);
    this.pos = sub.pos;
    for (const e of sub.errors) this.errors.push({ ...e });
    return decl;
  }

  /** Delegate a `view` declaration to the v0.10 sub-parser (ordered clauses). */
  private parseV010View(): ViewDeclV2 {
    const sub = new V010DeclParser(this.tokens, this.pos);
    sub.rawSource = this.source;
    const decl = sub.parseView();
    this.pos = sub.pos;
    for (const e of sub.errors) this.errors.push({ ...e });
    return decl;
  }

  /**
   * A `test` declaration head: the contextual identifier `test` followed by a
   * `#id` or the name STRING. A bare `test {` stays a module named `test`; a
   * `test:`/`test.x` head stays a field — the string/`#id` disambiguates.
   */
  private isTestHeadAt(off: number): boolean {
    const head = off === 0 ? this.peek() : this.peekAhead(off);
    if (!(head?.type === "IDENTIFIER" && head.value === "test")) return false;
    const next = this.peekAhead(off + 1)?.type;
    return next === "STABLE_ID" || next === "STRING";
  }

  /** Delegate a `test` declaration to the v0.10 sub-parser, syncing pos + errors. */
  private parseV010Test(): TestDecl {
    const sub = new V010DeclParser(this.tokens, this.pos);
    sub.rawSource = this.source;
    const decl = sub.parseTest();
    this.pos = sub.pos;
    for (const e of sub.errors) this.errors.push({ ...e });
    return decl;
  }

  // -------------------------------------------------------------------------
  // Body (module / surface / type)
  // -------------------------------------------------------------------------

  private parseOptionalBody(): Body {
    if (!this.check("LBRACE")) return EMPTY_BODY;
    return this.parseBody();
  }

  /** `refSink`, when given, makes this body a PARTIAL-PRESENCE body (§1.2): a
   *  bare (dotted) name with nothing declaration-like after it is a REFERENCE to
   *  an existing member rather than a declaration. Only the block's own members
   *  are references — nested bodies parse normally (they declare). */
  private parseBody(refSink?: PresenceRef[]): Body {
    this.consume("LBRACE", "parse.expectedOpenGot");

    const fields: FieldDecl[] = [];
    const aspects: AspectEntry[] = [];
    const descriptions: DescriptionEntry[] = [];
    const drops: DropStmt[] = [];
    const surfaces: SurfaceDecl[] = [];
    const interfaces: InterfaceDecl[] = [];
    const modules: ModuleDecl[] = [];
    const processes: ProcessDecl[] = [];
    const subprocesses: SubprocessDecl[] = [];
    const rosters: AspectRoster[] = [];
    const alsoOn: QualifiedName[] = [];
    const partialOn: PartialPresence[] = [];
    const views: ViewDeclV2[] = [];
    const policies: PolicyDecl[] = [];
    const queries: QueryDecl[] = [];
    const tests: TestDecl[] = [];
    const lenses: LensDecl[] = [];

    let prevMemberEnd: Token | null = null;
    while (!this.isAtEnd() && !this.check("RBRACE")) {
      // v0.9.0: members must be separated by ';' or a newline (consistent
      // with process one-liners; ',' now separates list values). After the
      // first member, consume any ';' separators; if none was present,
      // require that the next member began on a new line.
      if (prevMemberEnd) {
        const sawSep = this.consumeMemberSeparators("members");
        if (this.check("RBRACE") || this.isAtEnd()) break;
        if (!sawSep && !this.lineBreakBetween(prevMemberEnd, this.peek())) {
          this.error(this.peek(), "parse.sepBetweenNewline", { noun: 'members' });
        }
      }
      if (
        this.parseBodyMember(
          fields,
          aspects,
          descriptions,
          drops,
          surfaces,
          interfaces,
          modules,
          processes,
          subprocesses,
          rosters,
          alsoOn,
          partialOn,
          views,
          policies,
          queries,
          tests,
          lenses,
          refSink
        )
      ) {
        prevMemberEnd = this.previous();
        continue;
      }
      // Recover: skip token to avoid infinite loop. Drop the separator
      // requirement across the recovered junk to avoid cascading errors.
      this.advance();
      prevMemberEnd = null;
    }

    this.consume("RBRACE", "parse.expectedCloseToGot", { what: 'body' });

    return {
      fields,
      aspects,
      ...(rosters.length ? { rosters } : {}),
      ...(alsoOn.length ? { alsoOn } : {}),
      ...(partialOn.length ? { partialOn } : {}),
      descriptions,
      drops,
      surfaces,
      interfaces,
      modules,
      processes,
      subprocesses,
      ...(views.length ? { views } : {}),
      ...(policies.length ? { policies } : {}),
      ...(queries.length ? { queries } : {}),
      ...(tests.length ? { tests } : {}),
      ...(lenses.length ? { lenses } : {}),
    };
  }

  /**
   * Parse one body member into the appropriate output array. Returns true
   * if anything was consumed; false on parse error so the caller can recover.
   */
  private parseBodyMember(
    fields: FieldDecl[],
    aspects: AspectEntry[],
    descriptions: DescriptionEntry[],
    drops: DropStmt[],
    surfaces: SurfaceDecl[],
    interfaces: InterfaceDecl[],
    modules: ModuleDecl[],
    processes: ProcessDecl[],
    subprocesses: SubprocessDecl[],
    rosters: AspectRoster[] = [],
    alsoOn: QualifiedName[] = [],
    partialOn: PartialPresence[] = [],
    views: ViewDeclV2[] = [],
    policies: PolicyDecl[] = [],
    queries: QueryDecl[] = [],
    tests: TestDecl[] = [],
    lenses: LensDecl[] = [],
    refSink?: PresenceRef[]
  ): boolean {
    // `@name(args)` above a body member — buffer, parse the member it prefixes,
    // then stamp whichever collection just grew. Same outermost-prefix rule as
    // the top level; body members have no `export`/`on` prefix to order against.
    if (this.check("AT")) {
      const at = this.peek();
      const decorations = this.parseDecorationPrefix();
      const decorated: { arr: { decorations?: readonly DecorationInstance[] }[]; len: number }[] = [
        { arr: fields, len: fields.length },
        { arr: aspects, len: aspects.length },
        { arr: surfaces, len: surfaces.length },
        { arr: interfaces, len: interfaces.length },
        { arr: modules, len: modules.length },
        { arr: processes, len: processes.length },
        { arr: subprocesses, len: subprocesses.length },
      ];
      const ok = this.parseBodyMember(
        fields, aspects, descriptions, drops, surfaces, interfaces, modules,
        processes, subprocesses, rosters, alsoOn, partialOn, views, policies,
        queries, tests, lenses, refSink,
      );
      const grown = decorated.find((d) => d.arr.length > d.len);
      if (grown) {
        const i = grown.arr.length - 1;
        grown.arr[i] = { ...grown.arr[i], decorations };
      } else if (ok) {
        this.error(at, "parse.decorationNotAttachable", {
          kind: "member",
          name: decorations[0]?.name ?? "",
        });
      }
      return ok;
    }

    // Bare description string
    if (this.check("STRING")) {
      const tok = this.advance();
      descriptions.push({ text: tok.value, source: tok.span });
      return true;
    }

    // v0.9.0 §1.3/§1.4: `aspect key: value`, `aspect { … }`, `aspect key for …`
    if (this.check("ASPECT")) {
      this.parseAspectMember(aspects, rosters);
      return true;
    }
    // v0.9.0 §1.2: body presence — `on p1, p2` (also-on, additive) or
    // `on p { …members… }` (partial presence: only listed members ride).
    if (this.check("ON")) {
      this.advance();
      const planes = this.parsePlaneList();
      if (this.check("LBRACE")) {
        if (planes.length !== 1) {
          this.error(this.peek(), "parse.partialPresenceOnePlane", { frag: "`on <plane> { … }`" });
        }
        const planeTok = this.previous();
        const pStart = planeTok.span;
        const refs: PresenceRef[] = [];
        const partialBody = this.parseBody(refs);
        // §1.2: partial-presence contents are fields / interfaces / submodules
        // only — aspects are node-level cross-plane facts, they don't ride a
        // presence block.
        if (partialBody.aspects.length || (partialBody.rosters?.length ?? 0)) {
          this.error(planeTok, "parse.partialPresenceNoAspects", { frag: "`on <plane> { … }`" });
        }
        partialOn.push({
          plane: planes[0] ?? [],
          body: partialBody,
          ...(refs.length ? { refs } : {}),
          planeSource: pStart,
          source: { start: pStart.start, end: this.previous().span.end, file: pStart.file },
        });
      } else {
        alsoOn.push(...planes);
      }
      return true;
    }
    // One-cycle migration error: `aspects` was renamed to `aspect`. Recover by
    // parsing the OLD forms so downstream stays functional while red.
    if (this.check("LABELS")) {
      this.error(this.peek(), "parse.labelsRenamedAspectValue", { frag: "`aspect key: value` / `aspect { … }`" });
      if (this.peekN(1)?.type === "DOT") {
        aspects.push(this.parseDottedLabel(false));
        return true;
      }
      this.parseLabelsBlock(aspects);
      return true;
    }

    // drop <qualified-name>
    if (this.check("DROP")) {
      drops.push(this.parseDropStmt());
      return true;
    }

    // v0.8.2 §4.16: placement/extension is a TOP-LEVEL statement — it does not
    // nest inside a body. A stray `in <Target>` here is an error; recover by
    // dropping the clause so the following declaration still nests lexically.
    if (this.check("IN")) {
      const inTok = this.advance();
      this.parseQualifiedName();
      this.error(inTok, "parse.inPlacementTopLevelOnly");
      return true;
    }

    // override <decl>
    if (this.check("OVERRIDE")) {
      this.parseOverrideDecl(surfaces, interfaces, modules);
      return true;
    }

    // export <interface> / export subprocess — publish across the space
    // boundary (v0.5). `export` may only prefix an interface or subprocess.
    if (this.check("EXPORT")) {
      const exp = this.advance();
      // In a BODY, `on` opens a presence form — also-on, or a partial-presence
      // block — not a born-on prefix as it does at top level. Neither is a
      // declaration, so there is nothing for `export` to apply to; the members
      // inside the block take it individually. Say that once and recover by
      // parsing the presence form as written, instead of falling through to the
      // generic complaint plus two cascading separator errors.
      if (this.check("ON")) {
        this.error(exp, "parse.exportOnPresenceInBody");
        return this.parseBodyMember(
          fields, aspects, descriptions, drops, surfaces, interfaces, modules, processes,
          subprocesses, rosters, alsoOn, partialOn, views, policies, queries, tests, lenses,
          refSink
        );
      }
      // `source` stays on the decl; the `export` prefix is recorded in
      // `extentSource` so delete/move consume it (never orphan the keyword).
      const exportExtent = (declSource: SourceSpan | undefined): SourceSpan | undefined =>
        declSource ? { start: exp.span.start, end: declSource.end, file: exp.span.file } : undefined;
      if (this.check("SUBPROCESS")) {
        const decl = this.parseSubprocessDecl();
        subprocesses.push({ ...decl, exported: true, extentSource: exportExtent(decl.source) });
        return true;
      }
      // `export process` in a module body — a module-scoped process is not
      // exportable (a space's published surface is declared at its root, §4.11).
      // Recover by keeping the process un-exported so the body still resolves.
      if (this.check("PROCESS")) {
        this.error(exp, "parse.exportModuleScopedProcess");
        const proc = this.parseProcessDecl();
        processes.push(proc.parentPath ? { ...proc, parentPath: undefined, parentSource: undefined } : proc);
        return true;
      }
      const member = this.check("IDENTIFIER")
        ? this.parseFieldOrSubDecl(fields, aspects)
        : null;
      if (member && member.kind === "interface") {
        interfaces.push({ ...member.decl, exported: true, extentSource: exportExtent(member.decl.source) });
        return true;
      }
      // A nested module exports the same way a top-level one does — `export` on
      // a module sanctions being named as a PLACE from another space (§1.3).
      if (member && member.kind === "module") {
        modules.push({ ...member.decl, exported: true, extentSource: exportExtent(member.decl.source) });
        return true;
      }
      this.error(exp, "parse.exportInterfaceOrSubprocess");
      if (member && member.kind === "surface") surfaces.push(member.decl);
      return true;
    }

    // Module-scoped process. Lexical placement names the owner; `in` here
    // would be redundant/ambiguous, so we flag it and ignore the clause.
    if (this.check("PROCESS")) {
      const proc = this.parseProcessDecl();
      if (proc.parentPath) {
        this.error(
          { span: proc.parentSource ?? proc.source! } as Token,
          "parse.inClauseBodyNestedProcess"
        );
        processes.push({ ...proc, parentPath: undefined, parentSource: undefined });
      } else {
        processes.push(proc);
      }
      return true;
    }

    // Module/type-scoped reusable subprocess. Inherited by instances when
    // declared on a `type module …` body; callable from any process owned
    // by the enclosing module (or a descendant).
    if (this.check("SUBPROCESS")) {
      subprocesses.push(this.parseSubprocessDecl());
      return true;
    }

    // v0.10 §5.2 member-head dispatch: a `view` head followed by ':'/'.' is a
    // FIELD named `view` (contextual-keyword doctrine, §4.9 — reserved words
    // stay usable as field keys); otherwise it declares a module-scoped view.
    if (this.check("VIEW")) {
      const next = this.peekAhead(1)?.type;
      if (next === "COLON" || next === "DOT") {
        this.parseKeywordHeadedField(fields, aspects);
      } else {
        views.push(this.parseV010View());
      }
      return true;
    }

    // Type-body modifiers — required / cascade / append before a field/decl
    if (this.check("REQUIRED") || this.check("CASCADE") || this.check("APPEND")) {
      this.parseTypeModifiedMember(fields, aspects, surfaces, interfaces, modules);
      return true;
    }

    // Process-tests: `test "…" { … }` — checked BEFORE the bare-step branch
    // (`test "name"` is `IDENT STRING`, which `isBareStepAhead` reads as a
    // call-with-comment step); a `test:`/`test.x` head stays a field.
    if (this.isTestHeadAt(0)) {
      tests.push(this.parseV010Test());
      return true;
    }
    // Bare step(s) written directly in the body → an anonymous process (§4.13).
    // Checked before the field/sub-decl branch: a leading `>` or an
    // `IDENT (.IDENT)* >` / `IDENT =` shape is a step, not a field or kind.
    if (this.isBareStepAhead()) {
      const decl = this.parseAnonymousProcess();
      if (decl) processes.push(decl);
      return true;
    }

    // v0.10 §5.2 member-head dispatch (module_member): the contextual heads
    // `query`/`policy` followed by a name declare here, exactly like the
    // top-level dispatch; a head followed by ':'/'.' falls through to the
    // field branch below (a field named `policy` stays reachable).
    if (this.isV010DeclHeadAt(0, "query")) {
      queries.push(this.parseV010Query(false));
      return true;
    }
    if (this.isV010DeclHeadAt(0, "policy")) {
      policies.push(this.parseV010Policy(false));
      return true;
    }
    // `lens {` → module-level lens block; `lens: x` / `lens.x: y` stay fields.
    if (this.isIdentValue(0, "lens") && this.peekAhead(1)?.type === "LBRACE") {
      lenses.push(this.parseV010Lens());
      return true;
    }

    // v0.9.0 §1.2: inside a partial-presence block, a bare (dotted) name with
    // nothing declaration-like after it REFERENCES a member declared in the main
    // body — that member rides to the plane and stays on primary. Same rule the
    // top-level `on` block uses for inclusions; a registered type name is still
    // a declaration (the §4.14 rule is not inverted — the resolver decides).
    if (
      refSink &&
      this.check("IDENTIFIER") &&
      !this.lookaheadIsFieldAssignment() &&
      this.isBareInclusionRef()
    ) {
      const start = this.peek().span;
      const path = this.parseQualifiedName();
      refSink.push({
        path,
        source: { start: start.start, end: this.previous().span.end, file: start.file },
      });
      return true;
    }

    // Identifier-led: either a field assignment or a sub-declaration
    if (this.check("IDENTIFIER")) {
      const member = this.parseFieldOrSubDecl(fields, aspects);
      if (!member) return false;
      switch (member.kind) {
        case "field":
          // The field decl has already been pushed by parseFieldOrSubDecl
          // (so any optional `{ ... }` body could push sub-decls right
          // after the root). Caller need not push again.
          return true;
        case "surface":
          surfaces.push(member.decl);
          return true;
        case "interface":
          interfaces.push(member.decl);
          return true;
        case "module":
          modules.push(member.decl);
          return true;
      }
    }

    this.error(this.peek(), "parse.unexpectedTokenIn", { context: "body", value: this.peek().value });
    return false;
  }

  /**
   * v0.9.0 §1.3/§1.4 — the `aspect` member forms (cursor ON the keyword):
   *   aspect key.path: value[, value…]        inline entry
   *   aspect { key: v; required k; drop k }   block (SINGULAR keyword)
   *   aspect key for: A as x, B               roster — place-side membership
   *   aspect key for { A as x; B }            roster block
   */
  private parseAspectMember(aspects: AspectEntry[], rosters: AspectRoster[]): void {
    const startSpan = this.consume("ASPECT", "parse.expectedThingGot", { thing: "'aspect'" }).span;
    if (this.check("LBRACE")) {
      this.parseAspectBlock(aspects);
      return;
    }
    if (this.check("DOT")) {
      // `aspect.key` — the keyword is not a path segment (§1.3).
      this.error(this.peek(), "parse.aspectNoDot", { frag: '`aspect key.path: value`' });
      this.advance();
    }
    const keyStart = this.peek().span;
    const key = this.parseQualifiedName();
    const keySource: SourceSpan = { start: keyStart.start, end: this.previous().span.end, file: keyStart.file };
    // §1.4: `for` between the key and `:`/`{` = roster. Slot-reserved — an
    // aspect key literally named `for` is disallowed (documented edge).
    if (
      this.check("IDENTIFIER") &&
      this.peek().value === "for" &&
      (this.peekAhead(1)?.type === "COLON" || this.peekAhead(1)?.type === "LBRACE")
    ) {
      this.advance(); // `for`
      const entries: { path: QualifiedName; instance?: string; source?: SourceSpan; instanceSource?: SourceSpan }[] = [];
      if (this.matchToken("COLON")) {
        for (;;) {
          const e = this.parseRosterEntry();
          if (e) entries.push(e);
          if (!this.check("COMMA") || this.lineBreakBetween(this.previous(), this.peek())) break;
          this.advance(); // ','
          if (!this.check("IDENTIFIER") && !this.checkMemberSegment()) {
            this.error(this.previous(), "parse.rosterTrailingComma");
            break;
          }
        }
      } else {
        this.consume("LBRACE", "parse.expectedColonOrOpenForGot");
        while (!this.isAtEnd() && !this.check("RBRACE")) {
          if (this.matchToken("SEMICOLON")) continue;
          const before = this.pos;
          const e = this.parseRosterEntry();
          if (e) entries.push(e);
          else if (this.pos === before) this.advance();
          this.matchToken("SEMICOLON");
        }
        this.consume("RBRACE", "parse.expectedCloseToGot", { what: 'roster' });
      }
      rosters.push({
        key,
        entries,
        keySource,
        source: { start: startSpan.start, end: this.previous().span.end, file: startSpan.file },
      });
      return;
    }
    // The value is optional — a bare `aspect domain` is a valueless entry
    // (a blank, e.g. declared by a type without `required`).
    let value: Value | undefined;
    if (this.matchToken("COLON")) {
      value = this.parseAspectValueList();
    }
    // Aspects are strictly scalar cascade-override (§4.4) — no sub-field
    // bodies on an aspect (label) path.
    if (this.check("LBRACE")) {
      this.error(this.peek(), "parse.aspectNoSubFieldBodies", { frag: "`aspect { … }`" });
      this.consumeBalancedBraces();
    }
    aspects.push({
      path: key,
      value,
      source: { start: startSpan.start, end: this.previous().span.end, file: startSpan.file },
      pathSource: keySource,
    });
  }

  /** One roster entry: a (possibly dotted) module reference + optional
   *  `as <instance>`. Strings are rejected — implicit places can't be members. */
  private parseRosterEntry():
    | { path: QualifiedName; instance?: string; source?: SourceSpan; instanceSource?: SourceSpan }
    | undefined {
    if (this.check("STRING")) {
      this.error(this.peek(), "parse.rosterEntryModuleRef");
      this.advance();
      return undefined;
    }
    if (!this.check("IDENTIFIER") && !this.checkMemberSegment()) {
      this.error(this.peek(), "parse.expectedRosterModuleRef");
      return undefined;
    }
    const pStart = this.peek().span;
    const path = this.parseQualifiedName();
    let instance: string | undefined;
    let instanceSource: SourceSpan | undefined;
    if (this.matchToken("AS")) {
      const t = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: "instance name after 'as'" });
      instance = t.value;
      instanceSource = t.span;
    }
    return {
      path,
      instance,
      source: { start: pStart.start, end: this.previous().span.end, file: pStart.file },
      instanceSource,
    };
  }

  /** An aspect VALUE: string/number/bool/bracket-list via the generic value
   *  grammar, or a (possibly dotted) module reference with an optional
   *  `as <instance>` (§1.3). A single bare segment without `as` stays a plain
   *  identifier (today's reference-label shape).
   *
   *  `as` is part of the value grammar, so it parses after a STRING too
   *  (`aspect zone: "eu" as active`) — an implicit place has no scope to name an
   *  occupant in, and validation says so (`ASPECT_INSTANCE_ON_STRING`) instead
   *  of the parser failing with a generic "expected ';'". */
  private parseAspectValue(): Value {
    const t = this.peek().type;
    if (t === "STRING" || t === "NUMBER" || t === "TRUE" || t === "FALSE" || t === "LBRACKET") {
      const scalar = this.parseValue();
      if (scalar.kind !== "string" || !this.check("AS")) return scalar;
      this.advance();
      const tok = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: "instance name after 'as'" });
      return { ...scalar, instance: tok.value, instanceSource: tok.span };
    }
    if (!this.check("IDENTIFIER") && !this.checkMemberSegment()) {
      return this.parseValue(); // generic error + recovery
    }
    const pStart = this.peek().span;
    const path = this.parseQualifiedName();
    let instance: string | undefined;
    let instanceSource: SourceSpan | undefined;
    if (this.matchToken("AS")) {
      const tok = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: "instance name after 'as'" });
      instance = tok.value;
      instanceSource = tok.span;
    }
    const source: SourceSpan = { start: pStart.start, end: this.previous().span.end, file: pStart.file };
    if (path.length === 1 && !instance) return { kind: "identifier", text: path[0]!, source };
    return { kind: "ref", path, instance, source, instanceSource };
  }

  /** Comma list of aspects.values — same single-line rules as
   *  {@link parseValueList}, with ref-aware elements. */
  private parseAspectValueList(): Value {
    const first = this.parseAspectValue();
    if (!this.check("COMMA")) return first;
    const items: Value[] = [first];
    while (this.check("COMMA")) {
      if (this.lineBreakBetween(this.previous(), this.peek())) break;
      const comma = this.peek();
      const next1 = this.peekAhead(1);
      const nextOnNewLine = !next1 || this.lineBreakBetween(comma, next1);
      if (
        nextOnNewLine ||
        next1.type === "RBRACE" ||
        next1.type === "RBRACKET" ||
        next1.type === "SEMICOLON" ||
        next1.type === "EOF"
      ) {
        this.advance();
        this.error(comma, "parse.bareListOneLine");
        break;
      }
      // `x, key: v` — the token after the comma is the next entry's key
      // packed with ','; the separator diagnostic owns that comma. (A DOT
      // does NOT break here — aspect refs are legitimately dotted.)
      const next2 = this.peekAhead(2);
      if (next1.type === "IDENTIFIER" && next2?.type === "COLON") break;
      this.advance(); // ','
      if (!this.isValueStartType(this.peek().type)) {
        this.error(comma, "parse.expectedValueAfterComma");
        break;
      }
      items.push(this.parseAspectValue());
    }
    if (items.length === 1) return first;
    const firstSpan = first.source ?? this.previous().span;
    return {
      kind: "list",
      items,
      source: { start: firstSpan.start, end: this.previous().span.end, file: firstSpan.file },
    };
  }

  /** The `aspect { … }` block — entries `key: valueList`, `required key[:v]`,
   *  `drop key`; `;`/newline separated (cursor ON the '{'). */
  private parseAspectBlock(aspects: AspectEntry[]): void {
    this.consume("LBRACE", "parse.expectedOpenAfterGot", { after: "'aspect'" });
    let prevMemberEnd: Token | null = null;
    while (!this.isAtEnd() && !this.check("RBRACE")) {
      if (prevMemberEnd) {
        const sawSep = this.consumeMemberSeparators("labels");
        if (this.check("RBRACE") || this.isAtEnd()) break;
        if (!sawSep && !this.lineBreakBetween(prevMemberEnd, this.peek())) {
          this.error(this.peek(), "parse.sepBetweenNewline", { noun: 'aspects' });
        }
      } else {
        while (this.matchToken("SEMICOLON")) { /* skip leading separators */ }
        if (this.check("RBRACE") || this.isAtEnd()) break;
      }
      if (this.matchToken("DROP")) {
        const startSpan = this.previous().span;
        const pathStart = this.peek().span;
        const path = this.parseQualifiedName();
        aspects.push({
          path,
          drop: true,
          source: { start: startSpan.start, end: this.previous().span.end, file: startSpan.file },
          pathSource: { start: pathStart.start, end: pathStart.end, file: pathStart.file },
        });
        prevMemberEnd = this.previous();
        continue;
      }
      if (this.matchToken("REQUIRED")) {
        const startSpan = this.previous().span;
        const pathStart = this.peek().span;
        const path = this.parseQualifiedName();
        let value: Value | undefined;
        if (this.matchToken("COLON")) value = this.parseAspectValueList();
        aspects.push({
          path,
          value,
          required: true,
          source: { start: startSpan.start, end: this.previous().span.end, file: startSpan.file },
          pathSource: { start: pathStart.start, end: pathStart.end, file: pathStart.file },
        });
        prevMemberEnd = this.previous();
        continue;
      }
      if (this.check("IDENTIFIER")) {
        const pathStart = this.peek().span;
        const path = this.parseQualifiedName();
        this.consume("COLON", "parse.expectedAfterGot", { expected: "':'", after: 'aspect key' });
        const value = this.parseAspectValueList();
        aspects.push({
          path,
          value,
          source: { start: pathStart.start, end: this.previous().span.end, file: pathStart.file },
          pathSource: { start: pathStart.start, end: pathStart.end, file: pathStart.file },
        });
        prevMemberEnd = this.previous();
        continue;
      }
      this.error(this.peek(), "parse.unexpectedTokenIn", { context: "aspect block", value: this.peek().value });
      this.advance();
      prevMemberEnd = null;
    }
    this.consume("RBRACE", "parse.expectedCloseToGot", { what: 'aspect block' });
  }

  private parseLabelsBlock(aspects: AspectEntry[]): void {
    this.consume("LABELS", "parse.expectedThingGot", { thing: "'labels'" });
    this.consume("LBRACE", "parse.expectedOpenAfterGot", { after: "'labels'" });

    let prevMemberEnd: Token | null = null;
    while (!this.isAtEnd() && !this.check("RBRACE")) {
      // v0.9.0: entries must be ';'- or newline-separated (',' separates
      // list values). After the first entry, consume any ';'; if none,
      // require the next entry to begin on a new line (`aspects { a: 1 b: 2 }`
      // on one line is rejected). Leading and repeated ';' are skipped
      // leniently.
      if (prevMemberEnd) {
        const sawSep = this.consumeMemberSeparators("labels");
        if (this.check("RBRACE") || this.isAtEnd()) break;
        if (!sawSep && !this.lineBreakBetween(prevMemberEnd, this.peek())) {
          this.error(this.peek(), "parse.sepBetweenNewline", { noun: 'aspects' });
        }
      } else {
        while (this.matchToken("SEMICOLON")) { /* skip leading separators */ }
        if (this.check("RBRACE") || this.isAtEnd()) break;
      }
      // drop X (inside aspects block)
      if (this.matchToken("DROP")) {
        const startSpan = this.previous().span;
        const pathStart = this.peek().span;
        const path = this.parseQualifiedName();
        aspects.push({
          path,
          drop: true,
          source: { start: startSpan.start, end: this.previous().span.end, file: startSpan.file },
          pathSource: { start: pathStart.start, end: this.previous().span.end, file: pathStart.file },
        });
        prevMemberEnd = this.previous();
        continue;
      }
      // required X (inside aspects block)
      if (this.matchToken("REQUIRED")) {
        const startSpan = this.previous().span;
        const pathStart = this.peek().span;
        const path = this.parseQualifiedName();
        let value: Value | undefined;
        if (this.matchToken("COLON")) value = this.parseValueList();
        aspects.push({
          path,
          value,
          required: true,
          source: { start: startSpan.start, end: this.previous().span.end, file: startSpan.file },
          pathSource: { start: pathStart.start, end: pathStart.end, file: pathStart.file },
        });
        prevMemberEnd = this.previous();
        continue;
      }
      // X: value
      if (this.check("IDENTIFIER")) {
        const pathStart = this.peek().span;
        const path = this.parseQualifiedName();
        this.consume("COLON", "parse.expectedAfterGot", { expected: "':'", after: 'label key' });
        const value = this.parseValueList();
        aspects.push({
          path,
          value,
          source: { start: pathStart.start, end: this.previous().span.end, file: pathStart.file },
          pathSource: { start: pathStart.start, end: pathStart.end, file: pathStart.file },
        });
        prevMemberEnd = this.previous();
        continue;
      }
      this.error(this.peek(), "parse.unexpectedTokenIn", { context: "aspects block", value: this.peek().value });
      this.advance();
      prevMemberEnd = null;
    }

    this.consume("RBRACE", "parse.expectedCloseToGot", { what: 'aspects block' });
  }

  private parseDropStmt(): DropStmt {
    const start = this.consume("DROP", "parse.expectedThingGot", { thing: "'drop'" }).span;
    const targetStart = this.peek().span;
    const target = this.parseQualifiedName();
    return {
      target,
      source: { start: start.start, end: this.previous().span.end, file: start.file },
      targetSource: { start: targetStart.start, end: this.previous().span.end, file: targetStart.file },
    };
  }

  private parseOverrideDecl(
    surfaces: SurfaceDecl[],
    interfaces: InterfaceDecl[],
    modules: ModuleDecl[]
  ): void {
    this.consume("OVERRIDE", "parse.expectedThingGot", { thing: "'override'" });
    // The next thing is either `required <decl>` or a kind-led declaration.
    let required = false;
    if (this.matchToken("REQUIRED")) required = true;

    if (!this.check("IDENTIFIER")) {
      this.error(this.peek(), "parse.expectedTypeAfterOverride");
      return;
    }

    // Parse as a sub-declaration with the 'override' marker.
    const sub = this.parseSubDecl();
    if (!sub) return;
    if (sub.kind === "surface") {
      surfaces.push({ ...sub.decl, override: true, required });
    } else if (sub.kind === "interface") {
      interfaces.push({ ...sub.decl, override: true, required });
    } else {
      modules.push({ ...sub.decl, override: true, required });
    }
  }

  /**
   * Parse the body member that begins with one or more type-body modifiers
   * (`required` / `cascade` / `append`). After modifiers come either:
   *   - a field path (with optional `:value`), or
   *   - a sub-declaration (kind + name).
   *
   * Disambiguation:
   *   - If `cascade` or `append` is present, it MUST be a field/label.
   *   - If only `required`: peek past the dotted path; another IDENTIFIER
   *     at that position means it's a sub-declaration (kind + name);
   *     otherwise it's a field/label.
   *   - A path that starts with `aspects.` (or via the LABELS keyword + DOT)
   *     is routed to the aspects collection rather than fields.
   */
  private parseTypeModifiedMember(
    fields: FieldDecl[],
    aspects: AspectEntry[],
    surfaces: SurfaceDecl[],
    interfaces: InterfaceDecl[],
    modules: ModuleDecl[]
  ): void {
    const startSpan = this.peek().span;
    let required = false;
    let cascade = false;
    let append = false;
    let group = false;

    while (true) {
      if (this.matchToken("REQUIRED")) {
        if (required) this.error(this.previous(), "parse.duplicateModifier", { modifier: 'required' });
        required = true;
        continue;
      }
      if (this.matchToken("CASCADE")) {
        if (cascade) this.error(this.previous(), "parse.duplicateModifier", { modifier: 'cascade' });
        if (append) this.error(this.previous(), "parse.cascadeAppendExclusive");
        cascade = true;
        // `cascade *` — group-root marker. The `*` is a sub-modifier
        // of `cascade`; only valid in that position so we don't
        // shadow other `*` uses (e.g. `use * from`).
        if (this.matchToken("STAR")) {
          group = true;
        }
        continue;
      }
      if (this.matchToken("APPEND")) {
        if (append) this.error(this.previous(), "parse.duplicateModifier", { modifier: 'append' });
        if (cascade) this.error(this.previous(), "parse.cascadeAppendExclusive");
        append = true;
        continue;
      }
      break;
    }

    if (group && !cascade) {
      this.error(this.previous(), "parse.groupMarkerNeedsCascade");
    }

    // v0.9.0: `required aspect domain[: value]` — the aspect blank/entry form.
    if (this.check("ASPECT")) {
      if (cascade || append) {
        this.error(this.peek(), "parse.aspectNoCascadeReason");
      }
      const startSpan = this.advance().span;
      const pathStart = this.peek().span;
      const path = this.parseQualifiedName();
      let value: Value | undefined;
      if (this.matchToken("COLON")) value = this.parseAspectValueList();
      aspects.push({
        path,
        value,
        ...(required ? { required: true } : {}),
        source: { start: startSpan.start, end: this.previous().span.end, file: startSpan.file },
        pathSource: { start: pathStart.start, end: pathStart.end, file: pathStart.file },
      });
      return;
    }
    // Legacy aspects-path member — one-cycle migration error, old parse for recovery.
    if (this.check("LABELS") && this.peekN(1)?.type === "DOT") {
      this.error(this.peek(), "parse.labelsRenamedAspectRequired", { frag: '`required aspect key`' });
      if (cascade || append) {
        this.error(this.peek(), "parse.aspectNoCascade");
      }
      aspects.push(this.parseDottedLabel(required));
      return;
    }

    if (!this.check("IDENTIFIER")) {
      this.error(this.peek(), "parse.expectedFieldOrSubDecl");
      return;
    }

    // With `cascade`/`append`, it MUST be a field. With only `required`,
    // peek past the dotted path: another IDENTIFIER => sub-decl, otherwise
    // it's a field/label-blank.
    const isField = cascade || append || !this.lookaheadIsSubDeclAfterPath();

    if (isField) {
      const pathStart = this.peek().span;
      const path = this.parseQualifiedName();

      let value: Value | undefined;
      if (this.matchToken("COLON")) {
        // Value is optional after `:` when followed by `{ ... }` body
        // (e.g. `cascade widget: { icon: x }`) — the field then has
        // no scalar value, only sub-fields.
        if (!this.check("LBRACE")) {
          value = this.parseValueList();
        }
      }

      const hasBody = this.check("LBRACE");
      // A bare field declaration (no value, no required, no cascade/append,
      // no body) has no semantic content and is rejected.
      if (!required && value === undefined && !cascade && !append && !hasBody) {
        this.error(this.previous(), "parse.fieldNeedsValue");
      }
      if (required && value !== undefined) {
        this.error(this.previous(), "parse.requiredFieldNoValue");
      }

      const end = this.previous().span;
      const pathSource = { start: pathStart.start, end: pathStart.end, file: pathStart.file };

      // Path starting with "aspects" routes to label entry.
      if (path[0] === "aspects" && path.length > 1) {
        // Label entries don't support sub-field bodies — aspects are
        // strictly scalar cascade-override (§4.4). A `aspects.X: v
        // { ... }` would imply nested compositional structure that
        // aspects don't have.
        if (this.check("LBRACE")) {
          this.error(this.peek(), "parse.aspectNoSubFieldBodies", { frag: "`aspect { … }`" });
          this.consumeBalancedBraces();
        }
        aspects.push({
          path: path.slice(1),
          value,
          required: required || undefined,
          source: { start: startSpan.start, end: end.end, file: startSpan.file },
          pathSource,
        });
        return;
      }

      fields.push({
        path,
        value,
        required: required || undefined,
        cascade: cascade || undefined,
        append: append || undefined,
        group: group || undefined,
        source: { start: startSpan.start, end: end.end, file: startSpan.file },
        pathSource,
      });

      // Optional field body: `widget: <v> { icon: x; color: y }`.
      // Each entry inside becomes a separate FieldDecl with `path` as
      // a prefix. When the enclosing field was declared `cascade *`,
      // sub-fields implicitly inherit `cascade: true` (the group's
      // job is to bundle them). Otherwise they're plain local fields
      // — the body is just a syntactic shortcut for repeating the
      // prefix on each line.
      if (this.check("LBRACE")) {
        this.parseFieldBody(path, group, fields, aspects);
      }
      return;
    }

    // Sub-declaration with `required`. cascade/append are field-only.
    if (cascade || append) {
      this.error(this.peek(), "parse.cascadeAppendFieldsOnly");
    }

    const sub = this.parseSubDecl();
    if (!sub) return;
    if (sub.kind === "surface") surfaces.push({ ...sub.decl, required: required || undefined });
    else if (sub.kind === "interface") interfaces.push({ ...sub.decl, required: required || undefined });
    else modules.push({ ...sub.decl, required: required || undefined });
  }

  /**
   * After a dotted IDENTIFIER path, is the next token another IDENTIFIER?
   * Used to disambiguate `required <field>` from `required <kind> <name>`.
   */
  /**
   * Parse the `{ … }` body that follows a `<field>: <value>` (or
   * `cascade * <field>: <value>`) declaration. Each entry inside is
   * a field/label declaration with `prefix` as its path prefix —
   * shorthand for repeating the prefix on every line.
   *
   * When `groupMode` is true (the enclosing field used `cascade *`),
   * sub-fields auto-inherit `cascade: true`, and authors may NOT
   * repeat `cascade` on members (it's implicit). When `groupMode` is
   * false, sub-fields are plain local fields and may carry any of
   * the usual modifiers (`required`, `cascade`, `append`).
   *
   * Sub-fields may not declare their own `*` group sigil — groups
   * don't nest in this iteration.
   */
  private parseFieldBody(
    prefix: QualifiedName,
    groupMode: boolean,
    fields: FieldDecl[],
    aspects: AspectEntry[],
  ): void {
    if (!this.matchToken("LBRACE")) {
      this.error(this.peek(), "parse.expectedOpenFieldBody", { open: OPEN_BRACE });
      return;
    }
    let prevMemberEnd: Token | null = null;
    while (!this.check("RBRACE") && !this.isAtEnd()) {
      // Sub-fields must be ';'- or newline-separated, same as top-level
      // body members — `{ a:b c:d }` on one line is rejected.
      if (prevMemberEnd) {
        const sawSep = this.consumeMemberSeparators("sub-fields");
        if (this.check("RBRACE") || this.isAtEnd()) break;
        if (!sawSep && !this.lineBreakBetween(prevMemberEnd, this.peek())) {
          this.error(this.peek(), "parse.sepBetweenNewline", { noun: 'sub-fields' });
        }
      }
      // A field SUB-member cannot be decorated (P0 scope: `field` as a target
      // kind means a top-level field). Recognised here so the message is
      // precise instead of a generic "unexpected token".
      if (this.check("AT")) {
        const at = this.peek();
        const dropped = this.parseDecorationPrefix();
        this.error(at, "parse.decorationOnSubField", { name: dropped[0]?.name ?? "" });
      }
      const memberStart = this.peek().span;
      let required = false;
      let cascade = false;
      let append = false;
      while (true) {
        if (this.matchToken("REQUIRED")) {
          if (required) this.error(this.previous(), "parse.duplicateModifier", { modifier: 'required' });
          required = true;
          continue;
        }
        if (this.matchToken("APPEND")) {
          if (append) this.error(this.previous(), "parse.duplicateModifier", { modifier: 'append' });
          append = true;
          continue;
        }
        if (this.matchToken("CASCADE")) {
          if (groupMode) {
            this.error(this.previous(), "parse.subFieldCascadeImplicit");
          } else {
            if (cascade) this.error(this.previous(), "parse.duplicateModifier", { modifier: 'cascade' });
            cascade = true;
          }
          continue;
        }
        break;
      }
      if (!this.check("IDENTIFIER")) {
        this.error(this.peek(), "parse.expectedSubFieldName");
        break;
      }
      const pathStart = this.peek().span;
      const subPath = this.parseQualifiedName();
      let value: Value | undefined;
      if (this.matchToken("COLON")) value = this.parseValueList();
      if (!required && value === undefined && !append && !cascade) {
        this.error(this.previous(), "parse.subFieldNeedsValue");
      }
      if (required && value !== undefined) {
        this.error(this.previous(), "parse.requiredSubFieldNoValue");
      }
      const memberEnd = this.previous().span;
      const fullPath = [...prefix, ...subPath];
      const pathSource = { start: pathStart.start, end: pathStart.end, file: pathStart.file };
      const source = { start: memberStart.start, end: memberEnd.end, file: memberStart.file };
      // `aspects.X` inside a body still routes to aspects — and
      // forbids its own body just like top-level label entries.
      if (fullPath[0] === "aspects" && fullPath.length > 1) {
        if (this.check("LBRACE")) {
          this.error(this.peek(), "parse.aspectNoSubFieldBodiesShort");
          this.consumeBalancedBraces();
        }
        aspects.push({
          path: fullPath.slice(1),
          value,
          required: required || undefined,
          source,
          pathSource,
        });
      } else {
        fields.push({
          path: fullPath,
          value,
          required: required || undefined,
          // Sub-fields under a cascade-group root auto-cascade; sub-
          // fields in a plain field body only cascade when the
          // author explicitly wrote `cascade`.
          cascade: groupMode || cascade || undefined,
          append: append || undefined,
          source,
          pathSource,
        });
        // Recurse: this sub-field may itself have a body. Inherits
        // groupMode from the enclosing body — if we're inside a
        // cascade-group, nested bodies are still part of the group.
        if (this.check("LBRACE")) {
          this.parseFieldBody(fullPath, groupMode, fields, aspects);
        }
      }
      prevMemberEnd = this.previous();
    }
    if (!this.matchToken("RBRACE")) {
      this.error(this.peek(), "parse.expectedCloseFieldBody", { close: CLOSE_BRACE });
    }
  }

  /** Skip past `{ … }` (balanced) — used to recover after an
   * unexpected group body so parsing of the enclosing body can
   * continue without cascading errors. */
  private consumeBalancedBraces(): void {
    if (!this.matchToken("LBRACE")) return;
    let depth = 1;
    while (depth > 0 && !this.isAtEnd()) {
      if (this.matchToken("LBRACE")) { depth++; continue; }
      if (this.matchToken("RBRACE")) { depth--; continue; }
      this.advance();
    }
  }

  private lookaheadIsSubDeclAfterPath(): boolean {
    let i = this.pos;
    if (this.tokens[i]?.type !== "IDENTIFIER") return false;
    i++;
    while (this.tokens[i]?.type === "DOT" && this.tokens[i + 1]?.type === "IDENTIFIER") {
      i += 2;
    }
    // After the path, an IDENTIFIER or STABLE_ID means a `<type> [#id] <name>`
    // sub-declaration — but ONLY when it sits on the SAME line as the path. The
    // lexer emits no newline token, so a following identifier on a LATER line
    // (a separate newline-separated member) would otherwise be misread as this
    // path's name, turning `required a.b.c` + next-line `rest_create x` into a
    // bogus dotted-type sub-decl. Compare line numbers to keep the field/blank
    // reading when the next member starts on its own line.
    const after = this.tokens[i];
    if (after?.type !== "IDENTIFIER" && after?.type !== "STABLE_ID") return false;
    const lastPathTok = this.tokens[i - 1];
    return after.span.start.line === lastPathTok!.span.start.line;
  }

  private parseDottedLabel(required: boolean): AspectEntry {
    // Caller has verified the next token is LABELS followed by DOT.
    const startSpan = this.peek().span;
    this.consume("LABELS", "parse.expectedThingGot", { thing: "'labels'" });
    this.consume("DOT", "parse.expectedAfterGot", { expected: "'.'", after: "'labels'" });
    const path = this.parseQualifiedName();
    let value: Value | undefined;
    if (this.matchToken("COLON")) value = this.parseValueList();
    // Labels don't support sub-field bodies — they're strictly scalar
    // cascade-override (§4.4). Block `aspects.X: v { ... }`.
    if (this.check("LBRACE")) {
      this.error(this.peek(), "parse.aspectNoSubFieldBodies", { frag: "`aspect { … }`" });
      this.consumeBalancedBraces();
    }
    const end = this.previous().span;
    return {
      path,
      value,
      required: required || undefined,
      source: { start: startSpan.start, end: end.end, file: startSpan.file },
      pathSource: { start: startSpan.start, end: end.end, file: startSpan.file },
    };
  }

  /**
   * Disambiguate `<identifier>...` between field assignment and
   * sub-declaration. Strategy: walk the dotted path, then check whether
   * the token immediately after the path is `:`.
   */
  private lookaheadIsFieldAssignment(): boolean {
    let i = this.pos;
    if (this.tokens[i]?.type !== "IDENTIFIER") return false;
    i++;
    while (this.tokens[i]?.type === "DOT" && this.tokens[i + 1]?.type === "IDENTIFIER") {
      i += 2;
    }
    return this.tokens[i]?.type === "COLON";
  }

  /**
   * Parse one of: a field assignment, a surface, an interface, or a sub-module.
   * Caller has confirmed the next token is IDENTIFIER (a kind name or field path).
   */
  private parseFieldOrSubDecl(
    fields: FieldDecl[],
    aspects: AspectEntry[],
  ):
    | { kind: "field" }
    | { kind: "surface"; decl: SurfaceDecl }
    | { kind: "interface"; decl: InterfaceDecl }
    | { kind: "module"; decl: ModuleDecl }
    | null {
    if (this.lookaheadIsFieldAssignment()) {
      const startSpan = this.peek().span;
      const path = this.parseQualifiedName();
      this.finishFieldAssignment(path, startSpan, fields, aspects);
      return { kind: "field" };
    }
    return this.parseSubDecl();
  }

  /**
   * The field-assignment tail shared by the ordinary IDENTIFIER-headed path and
   * the keyword-headed path (§5.2 member-head dispatch): cursor is ON the ':'
   * after the (already collected) dotted path.
   */
  private finishFieldAssignment(
    path: QualifiedName,
    startSpan: SourceSpan,
    fields: FieldDecl[],
    aspects: AspectEntry[]
  ): void {
    this.consume("COLON", "parse.expectedAfterGot", { expected: "':'", after: 'field path' });
    // Value is optional when an immediate `{ ... }` body follows
    // (e.g. `field: { sub: x }`) — the field acts as a pure
    // container with no scalar value, only sub-fields.
    let value: Value | undefined;
    if (!this.check("LBRACE")) {
      value = this.parseValueList();
    }
    const end = this.previous().span;
    const pathSource = { start: startSpan.start, end: startSpan.end, file: startSpan.file };
    const source = { start: startSpan.start, end: end.end, file: startSpan.file };
    // `aspects.X` always routes to aspects and may not carry a body.
    if (path[0] === "aspects" && path.length > 1) {
      if (this.check("LBRACE")) {
        this.error(this.peek(), "parse.aspectNoSubFieldBodies", { frag: "`aspect { … }`" });
        this.consumeBalancedBraces();
      }
      aspects.push({
        path: path.slice(1),
        value,
        source,
        pathSource,
      });
    } else {
      fields.push({
        path,
        value,
        source,
        pathSource,
      });
      // Plain field body: every entry inside becomes a separate
      // FieldDecl with `path` as prefix. Not a cascade group (no
      // `*`) so sub-fields stay local unless explicitly marked.
      if (this.check("LBRACE")) {
        this.parseFieldBody(path, false, fields, aspects);
      }
    }
  }

  /**
   * §5.2 member-head dispatch: a reserved-word head (`view`) followed by ':' or
   * '.' is a FIELD — the keyword serves as the first path segment (§4.9
   * contextual-keyword doctrine: field keys may be reserved words).
   */
  private parseKeywordHeadedField(fields: FieldDecl[], aspects: AspectEntry[]): void {
    const head = this.advance(); // the keyword token (e.g. `view`)
    const path: Name[] = [head.value];
    while (this.matchToken("DOT")) {
      path.push(this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: "identifier after '.'" }).value);
    }
    this.finishFieldAssignment(path, head.span, fields, aspects);
  }

  /**
   * Parse a sub-declaration: kind name + optional id (modules only) +
   * name + optional `in` + optional body. The decision between surface /
   * interface / module is made by the resolver based on the kind
   * registry; the parser only routes when the kind name matches the
   * built-in `surface` / `interface` keywords. Otherwise we tag it as
   * a module by default.
   *
   * Practically: the parser uses heuristics — kinds whose body contains
   * surfaces/interfaces/modules are modules; kinds whose body is leaf-only
   * are interfaces; the literal `surface` keyword (or kind named so) is a
   * surface. The resolver re-classifies at semantic time as needed.
   *
   * For v0.2 source, we use a simple heuristic against the arch.backend
   * interface kinds (`http_*`, `rest_*`, `grpc_*`, `graphql_*`,
   * `websocket`, `kafka`, `amqp`, `nats`, `mqtt`, `redis_pubsub`,
   * `webhook`, `sse`, `db_read`, `db_write`) plus their base names
   * (`http`, `rest`, `grpc`, `graphql`). `surface` is a surface; everything
   * else is a module. This is overridable when a project's stdlib
   * defines new interface kinds — those projects just need to use the
   * kind names that match this list, or the resolver will reclassify.
   */
  private parseSubDecl():
    | { kind: "surface"; decl: SurfaceDecl }
    | { kind: "interface"; decl: InterfaceDecl }
    | { kind: "module"; decl: ModuleDecl }
    | null {
    const typeTok = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: 'type identifier' });
    const typeName = typeTok.value;

    if (typeName === "interface" || isLikelyInterfaceType(typeName)) {
      const decl = this.finishInterfaceDecl(typeTok);
      return { kind: "interface", decl };
    }

    if (typeName === "surface" || isLikelySurfaceType(typeName)) {
      const decl = this.finishSurfaceDecl(typeTok);
      return { kind: "surface", decl };
    }

    const decl = this.finishModuleDecl(typeTok);
    return { kind: "module", decl };
  }

  /**
   * The instance name that follows a type, when present. A name counts only
   * when an IDENTIFIER follows on the SAME line as `prevTok` — a name on the
   * next line is the next declaration, leaving this one unnamed (§4.14).
   */
  private optionalInstanceName(prevTok: Token): Token | undefined {
    if (this.lineBreakBetween(prevTok, this.peek())) return undefined;
    if (this.check("IDENTIFIER")) {
      return this.advance();
    }
    return this.keywordNameHere(prevTok);
  }

  /**
   * v0.8.2 contextual keywords (SPEC §4.9): a reserved word sitting in a name
   * position (same line as `prevTok`) is a valid NAME — declaration names are
   * unrestricted, which chiefly enables lowercase interface/type names
   * (`rest_read select`, `kafka out`). `in` is excluded: it opens a placement
   * clause (`<type> in <Parent>` / `in <Target>`), not a name. The same-line
   * guard keeps a keyword on the NEXT line as the next declaration, never a name.
   * Returns the name token, or undefined when no same-line keyword name follows.
   */
  private keywordNameHere(prevTok: Token): Token | undefined {
    if (this.lineBreakBetween(prevTok, this.peek())) return undefined;
    const t = this.peek();
    if (t.type !== "IN" && RESERVED_KEYWORDS.has(t.value)) {
      return this.advance();
    }
    return undefined;
  }

  private finishSurfaceDecl(typeTok: Token): SurfaceDecl {
    // Surfaces may not be unnamed; an omitted name is still parsed (so it does
    // not steal the next declaration) and flagged `unnamed` for validation.
    const nameTok = this.optionalInstanceName(typeTok);
    const hasBody = this.check("LBRACE");
    const body = this.parseOptionalBody();
    const base = {
      typeName: typeTok.value,
      hasBody,
      body,
      source: { start: typeTok.span.start, end: this.previous().span.end, file: typeTok.span.file },
      typeSource: typeTok.span,
    };
    return nameTok
      ? { ...base, name: nameTok.value, nameSource: nameTok.span }
      : { ...base, name: deriveModuleNameFromType(typeTok.value), unnamed: true, nameSource: typeTok.span };
  }

  private finishInterfaceDecl(typeTok: Token): InterfaceDecl {
    // Interfaces may be unnamed only when braced — a braced unnamed interface
    // is the module's DEFAULT (§1.6); an unnamed bare `interface` is an error.
    const nameTok = this.optionalInstanceName(typeTok);
    const hasBody = this.check("LBRACE");
    const body = hasBody ? this.parseInterfaceBody() : EMPTY_INTERFACE_BODY;
    const base = {
      typeName: typeTok.value,
      hasBody,
      body,
      source: { start: typeTok.span.start, end: this.previous().span.end, file: typeTok.span.file },
      typeSource: typeTok.span,
    };
    return nameTok
      ? { ...base, name: nameTok.value, nameSource: nameTok.span }
      : { ...base, name: deriveModuleNameFromType(typeTok.value), unnamed: true, nameSource: typeTok.span };
  }

  private finishModuleDecl(typeTok: Token): ModuleDecl {
    const idTok = this.matchToken("STABLE_ID");
    const id = idTok ? stableId(idTok.value.slice(1)) : undefined;
    const idSource = idTok?.span;

    // The name is optional for a nested custom-typed module: an omitted name
    // derives from the type (snake_case → UpperCamelCase) and marks the module
    // `unnamed` (§4.14). It is a name only when an IDENTIFIER follows on the
    // same line as the type (or its `#id`).
    const nameTok = this.optionalInstanceName(idTok ?? typeTok);
    const name = nameTok ? nameTok.value : deriveModuleNameFromType(typeTok.value);
    const unnamed = nameTok === undefined;

    let parentPath: QualifiedName | undefined;
    let parentSource: SourceSpan | undefined;
    if (this.check("IN") && !this.lineBreakBetween(this.previous(), this.peek())) {
      // v0.8.2 §4.16: the suffix `<decl> in <Parent>` is removed — placement is
      // the `in <Parent> <decl>` prefix. Consume + flag so a stray suffix fails
      // loudly (never a silent mis-parse); the decl is left un-parented.
      const inTok = this.advance();
      this.parseQualifiedName();
      this.error(inTok, "parse.suffixInRemoved");
    }

    const hasBody = this.check("LBRACE");
    const body = this.parseOptionalBody();

    return {
      typeName: typeTok.value,
      id,
      name,
      hasBody,
      ...(unnamed ? { unnamed: true } : {}),
      parentPath,
      body,
      source: { start: typeTok.span.start, end: this.previous().span.end, file: typeTok.span.file },
      typeSource: typeTok.span,
      idSource,
      nameSource: nameTok ? nameTok.span : typeTok.span,
      parentSource,
    };
  }

  // -------------------------------------------------------------------------
  // Interface body
  // -------------------------------------------------------------------------

  private parseInterfaceBody(): InterfaceBody {
    this.consume("LBRACE", "parse.expectedOpenGot");

    const fields: FieldDecl[] = [];
    const aspects: AspectEntry[] = [];
    const descriptions: DescriptionEntry[] = [];
    const drops: DropStmt[] = [];

    let prevMemberEnd: Token | null = null;
    while (!this.isAtEnd() && !this.check("RBRACE")) {
      // v0.9.0: members must be ';'- or newline-separated, same as every
      // other body (previously unenforced here).
      if (prevMemberEnd) {
        const sawSep = this.consumeMemberSeparators("members");
        if (this.check("RBRACE") || this.isAtEnd()) break;
        if (!sawSep && !this.lineBreakBetween(prevMemberEnd, this.peek())) {
          this.error(this.peek(), "parse.sepBetweenNewline", { noun: 'members' });
        }
      }
      // `@name(args)` above an interface-body member. A field is the only
      // decorable member here (an interface body admits no sub-declarations).
      let pending: readonly DecorationInstance[] | undefined;
      if (this.check("AT")) {
        const at = this.peek();
        pending = this.parseDecorationPrefix();
        if (!this.check("IDENTIFIER")) {
          this.error(at, "parse.decorationNotAttachable", {
            kind: "member",
            name: pending[0]?.name ?? "",
          });
          pending = undefined;
        }
      }
      if (this.check("STRING")) {
        const tok = this.advance();
        descriptions.push({ text: tok.value, source: tok.span });
        prevMemberEnd = this.previous();
        continue;
      }
      if (this.check("ASPECT")) {
        // v0.9.0 §1.3: aspects are legal on interfaces (classification-only);
        // rosters are a module concept — reject any that parse here.
        const localRosters: AspectRoster[] = [];
        this.parseAspectMember(aspects, localRosters);
        if (localRosters.length) {
          this.error(this.previous(), "parse.rosterOnModuleNotInterface", { frag: '`aspect <key> for`' });
        }
        prevMemberEnd = this.previous();
        continue;
      }
      if (this.check("LABELS")) {
        this.error(this.peek(), "parse.labelsRenamedAspectValue", { frag: "`aspect key: value` / `aspect { … }`" });
        // aspects.X: value (dotted form) OR aspects { ... } block — old parse for recovery
        if (this.peekN(1)?.type === "DOT") {
          aspects.push(this.parseDottedLabel(false));
        } else {
          this.parseLabelsBlock(aspects);
        }
        prevMemberEnd = this.previous();
        continue;
      }
      if (this.check("DROP")) {
        drops.push(this.parseDropStmt());
        prevMemberEnd = this.previous();
        continue;
      }
      // Type-body modifiers might appear in interface bodies inside type defs
      if (this.check("REQUIRED") || this.check("CASCADE") || this.check("APPEND")) {
        // For simplicity, treat as field-only in interface bodies.
        const dummySurfaces: SurfaceDecl[] = [];
        const dummyInterfaces: InterfaceDecl[] = [];
        const dummyModules: ModuleDecl[] = [];
        this.parseTypeModifiedMember(fields, aspects, dummySurfaces, dummyInterfaces, dummyModules);
        if (dummySurfaces.length || dummyInterfaces.length || dummyModules.length) {
          this.error(this.previous(), "parse.interfaceNoSubDecl");
        }
        prevMemberEnd = this.previous();
        continue;
      }
      // Field assignment
      if (this.check("IDENTIFIER")) {
        if (this.lookaheadIsFieldAssignment()) {
          const pathStart = this.peek().span;
          const path = this.parseQualifiedName();
          this.consume("COLON", "parse.expectedAfterGot", { expected: "':'", after: 'field name' });
          const value = this.parseValueList();
          fields.push({
            ...(pending ? { decorations: pending } : {}),
            path,
            value,
            source: { start: pathStart.start, end: this.previous().span.end, file: pathStart.file },
            pathSource: { start: pathStart.start, end: pathStart.end, file: pathStart.file },
          });
          prevMemberEnd = this.previous();
          continue;
        }
      }

      this.error(this.peek(), "parse.unexpectedTokenIn", { context: "interface body", value: this.peek().value });
      this.advance();
      // Reset across recovered junk to avoid cascading separator errors.
      prevMemberEnd = null;
    }

    this.consume("RBRACE", "parse.expectedCloseToGot", { what: 'interface body' });
    return { fields, aspects, descriptions, drops };
  }

  // -------------------------------------------------------------------------
  // Values
  // -------------------------------------------------------------------------

  /**
   * v0.9.0: consume inline member separators. Body members packed on one
   * line separate with ';' (consistent with process one-liners); a ',' in
   * member position is diagnosed with a hint (it now separates list values)
   * and consumed for recovery. Returns true when any separator (or a
   * diagnosed comma) was consumed.
   */
  private consumeMemberSeparators(noun: string): boolean {
    let sawSep = false;
    while (this.matchToken("SEMICOLON")) sawSep = true;
    if (this.check("COMMA")) {
      this.error(this.peek(), "parse.sepBetweenComma", { noun });
      while (this.matchToken("COMMA") || this.matchToken("SEMICOLON")) {
        /* recover past the stray separators */
      }
      sawSep = true;
    }
    return sawSep;
  }

  /** True when a token of this type can begin a value. */
  private isValueStartType(t: TokenType): boolean {
    return (
      t === "STRING" ||
      t === "NUMBER" ||
      t === "TRUE" ||
      t === "FALSE" ||
      t === "LBRACKET" ||
      t === "IDENTIFIER" ||
      // §4.3: a getter in a value slot — `version: @major`, `widget.color: @@team`.
      t === "AT" ||
      t === "ATAT"
    );
  }

  /**
   * §4.3: is the value at the cursor an EXPRESSION rather than a plain literal?
   *
   * A getter sigil always is. A bare identifier only is when it is a registered
   * render cast applied to arguments (`colorize(@@team)`) — every other
   * identifier stays the literal it has always been, so `lifecycle: active` and
   * `widget: arch-module` keep their meaning.
   */
  private valueIsExpression(): boolean {
    const tok = this.peek();
    if (tok.type === "AT" || tok.type === "ATAT") return true;
    return (
      tok.type === "IDENTIFIER" &&
      EXPRESSION_FUNCTIONS.has(tok.value) &&
      this.peekN(1)?.type === "LPAREN"
    );
  }

  /**
   * Parse a getter/cast value by handing the cursor to the selector expression
   * parser — the SAME grammar a view's `style` body uses, sharing this token
   * array rather than re-lexing, so the two can never drift apart.
   */
  private parseExprValue(): Value {
    const start = this.peek();
    const sub = new SelectorExprParser(this.tokens, this.pos);
    if (this.source !== undefined) sub.rawSource = this.source;
    const expr = sub.parseExpression();
    this.pos = sub.pos;
    for (const e of sub.errors) {
      this.errors.push({ message: e.message, span: e.span ?? start.span, code: "PARSE_ERROR" });
    }
    const end = this.previous().span;
    return { kind: "expr", expr, source: { start: start.span.start, end: end.end, file: start.span.file } };
  }

  /**
   * A field/label VALUE position: one value, or a bare single-line comma
   * list (`tags: pii, audit`). Multiline lists use the bracket form (§3.2)
   * — a bare trailing ',' is an error pointing there. A comma followed by
   * what is really the NEXT member's key (`team: X, version: v2`) is left
   * unconsumed so the member-separator diagnostic fires with its hint.
   */
  private parseValueList(): Value {
    const first = this.parseValue();
    if (!this.check("COMMA")) return first;
    const items: Value[] = [first];
    while (this.check("COMMA")) {
      // A ',' on a later line is not list syntax — leave it for the member
      // separator logic to diagnose.
      if (this.lineBreakBetween(this.previous(), this.peek())) break;
      const comma = this.peek();
      const next1 = this.peekAhead(1);
      const next2 = this.peekAhead(2);
      // Trailing ',' — nothing valuable follows on the comma's own line.
      const nextOnNewLine = !next1 || this.lineBreakBetween(comma, next1);
      if (
        nextOnNewLine ||
        next1.type === "RBRACE" ||
        next1.type === "RBRACKET" ||
        next1.type === "EOF"
      ) {
        this.advance(); // consume the stray ','
        this.error(comma, "parse.bareListOneLine");
        break;
      }
      // A keyword-led member packed with ',' (`x, aspects { }`, `x, drop y`)
      // can never continue a list — leave the comma for the separator
      // diagnostic, which carries the "did you mean ';'" hint.
      if (!this.isValueStartType(next1.type)) break;
      // An identifier element is always followed by list machinery (',',
      // '}', ']', ';', a field body '{', or the end of the line). Anything
      // else — ':' / '.' (a packed key), another identifier (`db postgres`),
      // a string, … — means the token after the comma starts the NEXT
      // member; don't swallow it as a list element.
      if (
        next1.type === "IDENTIFIER" &&
        next2 &&
        !this.lineBreakBetween(next1, next2)
      ) {
        const t2 = next2.type;
        const elementFollow =
          t2 === "COMMA" ||
          t2 === "RBRACE" ||
          t2 === "RBRACKET" ||
          t2 === "SEMICOLON" ||
          t2 === "LBRACE" ||
          t2 === "EOF";
        if (!elementFollow) break;
      }
      this.advance(); // consume ','
      items.push(this.parseValue());
    }
    if (items.length === 1) return first;
    const firstSpan = first.source ?? this.previous().span;
    return {
      kind: "list",
      items,
      source: {
        start: firstSpan.start,
        end: this.previous().span.end,
        file: firstSpan.file,
      },
    };
  }

  private parseValue(): Value {
    const tok = this.peek();
    if (this.valueIsExpression()) return this.parseExprValue();
    if (tok.type === "STRING") {
      this.advance();
      return { kind: "string", text: tok.value, source: tok.span };
    }
    // v0.10: `-` is a uniform MINUS token; a signed number literal is a leading
    // `-` glued/spaced before a NUMBER in a value slot (pin args, knob defaults —
    // drag writes negatives). The sign is not lexed into the number.
    if (tok.type === "MINUS" && this.peekN(1)?.type === "NUMBER") {
      this.advance();
      const num = this.advance();
      return {
        kind: "number",
        value: -Number(num.value),
        source: { start: tok.span.start, end: num.span.end, file: tok.span.file },
      };
    }
    if (tok.type === "NUMBER") {
      this.advance();
      return { kind: "number", value: Number(tok.value), source: tok.span };
    }
    if (tok.type === "TRUE") {
      this.advance();
      return { kind: "boolean", value: true, source: tok.span };
    }
    if (tok.type === "FALSE") {
      this.advance();
      return { kind: "boolean", value: false, source: tok.span };
    }
    if (tok.type === "LBRACKET") {
      const startSpan = tok.span;
      this.advance();
      const items: Value[] = [];
      // Elements are separated by a comma OR a newline — the comma is optional
      // (adjacency separates, like body members). Trailing comma tolerated. The
      // lexer emits no newline token, so "newline-separated" is just adjacency.
      while (!this.check("RBRACKET") && !this.isAtEnd()) {
        items.push(this.parseValue());
        this.matchToken("COMMA"); // optional separator
      }
      this.consume("RBRACKET", "parse.expectedCloseBracketGot");
      return {
        kind: "list",
        items,
        source: { start: startSpan.start, end: this.previous().span.end, file: startSpan.file },
      };
    }
    if (tok.type === "IDENTIFIER") {
      this.advance();
      return { kind: "identifier", text: tok.value, source: tok.span };
    }
    this.error(tok, "parse.expectedValueGot", { got: tok.value || tok.type });
    this.advance();
    return { kind: "string", text: "", source: tok.span };
  }

  // -------------------------------------------------------------------------
  // Qualified names
  // -------------------------------------------------------------------------

  private parseQualifiedName(): QualifiedName {
    const parts: Name[] = [];
    const first = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: 'identifier' });
    parts.push(first.value);
    while (this.matchToken("DOT")) {
      const next = this.consume("IDENTIFIER", "parse.expectedThingGot", { thing: "identifier after '.'" });
      parts.push(next.value);
    }
    return parts;
  }

  // -------------------------------------------------------------------------
  // Token helpers
  // -------------------------------------------------------------------------

  private peek(): Token {
    return this.tokens[this.pos]!;
  }

  private peekN(n: number): Token | undefined {
    return this.tokens[this.pos + n];
  }

  private tokenAt(i: number): Token | undefined {
    return this.tokens[i];
  }

  private previous(): Token {
    return this.tokens[this.pos - 1]!;
  }

  /**
   * True if a newline appears in the trivia between two adjacent tokens.
   * Used to enforce that consecutive members (body members, sub-fields,
   * label entries) are separated by a comma or a newline — `{ a:b c:d }`
   * jammed together with only a space is rejected.
   */
  private lineBreakBetween(before: Token, after: Token): boolean {
    return (
      before.trailingTrivia.some((t) => t.text.includes("\n")) ||
      after.leadingTrivia.some((t) => t.text.includes("\n"))
    );
  }

  private isAtEnd(): boolean {
    return this.peek().type === "EOF";
  }

  private check(type: TokenType): boolean {
    return this.peek().type === type;
  }

  private advance(): Token {
    const tok = this.tokens[this.pos]!;
    if (!this.isAtEnd()) this.pos++;
    return tok;
  }

  private matchToken(type: TokenType): Token | null {
    if (this.check(type)) return this.advance();
    return null;
  }

  private consume(type: TokenType, msgKey: string, params?: Record<string, unknown>): Token {
    if (this.check(type)) return this.advance();
    const tok = this.peek();
    // The `{close}`/`{open}` templates need the expected bracket as a param;
    // derive it from the token type so no call site can forget it — a MISSING
    // ICU var makes the renderer fall back to the raw template and leak every
    // placeholder. Explicit params still win. Every `consume` template also ends
    // with the got-suffix, so the observed token rides along as `got`.
    const auto: Record<string, unknown> = {};
    if (type === "RBRACE") auto.close = CLOSE_BRACE;
    else if (type === "LBRACE") auto.open = OPEN_BRACE;
    this.error(tok, msgKey, { ...auto, ...params, got: tok.value || tok.type });
    // Synthesize a token to allow downstream to keep its shape.
    return tok;
  }

  private error(token: Token, msgKey: string, params?: Record<string, unknown>): void {
    this.errorAt(token.span, msgKey, params);
  }

  /** Emit a parse diagnostic anchored on an arbitrary span (not a raw token) —
   *  used when the offending construct is already parsed and only its
   *  source span survives. */
  private errorAt(span: SourceSpan, msgKey: string, params?: Record<string, unknown>): void {
    const message = renderDiagnostic(msgKey, params, "en") ?? msgKey;
    this.errors.push({ message, span, code: "PARSE_ERROR", msgKey, params });
  }

  private synchronizeToToplevel(): void {
    while (!this.isAtEnd()) {
      const t = this.peek().type;
      // `AT` / `DECORATION` are declaration heads too — without them a malformed
      // decoration swallows the declaration underneath it.
      if (
        t === "TYPE" ||
        t === "PROCESS" ||
        t === "VIEW" ||
        t === "DECORATION" ||
        t === "AT" ||
        t === "IDENTIFIER"
      )
        return;
      this.advance();
    }
  }
}

// =============================================================================
// Kind classification heuristics (parser-time)
// =============================================================================

function isLikelyInterfaceType(typeName: string): boolean {
  return STDLIB_INTERFACE_KINDS.has(typeName);
}

function isLikelySurfaceType(typeName: string): boolean {
  return STDLIB_SURFACE_KINDS.has(typeName);
}

/**
 * Derive an unnamed module's name from its type (§4.14): snake_case →
 * UpperCamelCase. `database → Database`, `payment_processor →
 * PaymentProcessor`, `db_v2 → DbV2`.
 */
export function deriveModuleNameFromType(typeName: string): string {
  return typeName
    .split("_")
    .filter((s) => s.length > 0)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join("");
}

// =============================================================================
// Re-exports
// =============================================================================
//
// tokenize/RESERVED_KEYWORDS are NOT re-exported here — lexer.ts is their
// sole owner. Re-exporting them too created an ambiguous `export *`
// collision in the engine barrel (index.ts star-exports both lexer.ts and
// parser.ts), which made the names unresolvable under the `development`
// export condition (tsx/Vite src-based dev boot).

export type { Token, TokenType };
